import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Stage, Layer, Rect, Circle, Line, Group, Text as KonvaText, Transformer, Label, Tag } from 'react-konva';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { addElement, transformElements, addElements, removeElements, selectTrackNode } from '../../features/layout/layoutSlice';
import { addHistoryAction } from '../../features/history/historySlice';
import {
  clearSelection,
  selectElement,
  selectElements,
} from '../../features/selection/selectionSlice';
import {
  setZoomLevel,
  setDxfLayersPanelOpen,
  setPlacingStopForVehicle,
  setTrackNodePlacementActive,
  setRouteEndpointPlacement,
  setRouteStartPoint,
  setRouteEndPoint,
  resetRouteEndpoints,
  clearPointDraw,
  addPointDrawVertex,
} from '../../features/ui/uiSlice';
import { activateCanvasTool } from '../../features/ui/activateCanvasTool';
import {
  completeEndpointCapture,
  cancelEndpointCapture,
} from '../../features/layout/routeEndpointCapture';
import { addVehicleStop } from '../../features/simulation/simulationSlice';
import { useViewport } from '../../hooks/useViewport';
import { viewportStore } from '../../store/viewportStore';
import { canvasChromeInsets, computeFitViewport } from '../../utils/flatPlanCamera';
import { LayoutElement, ElementType, ToolboxItem } from '../../types';
import {
  ELEMENT_COLORS,
  MIN_ZOOM,
  MAX_ZOOM,
  DEFAULT_ZOOM,
  GRID_SIZE,
  DEFAULT_TRACK_THICKNESS_METERS,
  DEFAULT_CURVE_RADIUS_METERS,
  DEFAULT_TRACK_DIRECTION,
  POINT_DRAW_DISCOVER_RADIUS_PX,
} from '../../constants';
import { generateId, snapPositionToGrid } from '../../utils/geometry';
import {
  getAABB,
  projectPointToNearestRail,
  railStopToWorld,
  getRailEndpoints,
  resolveRailType,
  isCurveRailType,
  getCurveSweepAngle,
  ENDPOINT_TOLERANCE,
  ALL_RAIL_TYPES,
  getStockerTrayPosition,
  snapEquipmentPolygonToNearestRail,
} from '../../utils/topology';
import {
  getLogicalPointDrawEndpoints,
  isOccupiedTrackInterior,
} from '../../utils/pointDrawEndpoints';
import { DEFAULT_LAYER_FOR_TYPE } from '../../features/layers/layersSlice';
import { defaultElementDepth, defaultElementZ } from '../../utils/layout3d';
import { selectRegion } from '../../features/facility/facilitySlice';
import { setFocusedPolyline } from '../../features/floorplan/floorplanSlice';
import {
  getEffectiveCalibration,
  worldUnitsToEffectiveMeters,
  formatMeasurementLabel,
  worldUnitsToEffectiveGrids,
  metersToEffectiveWorldUnits,
  shouldUseDxfCalibration,
  screenToWorld,
} from '../../utils/measurement';
import {
  computeSegmentBetween,
  buildOrthogonalTrackPath,
} from '../../utils/trackChain';
import { computeWheelZoomViewport } from '../../utils/canvasViewportGesture';
import {
  cleanupManhattanPolyline,
  closureApproachCandidates,
  isAxisAligned,
  resolvePointDrawCloseAnchor,
  segHeading,
  type CloseAnchor,
} from '../../utils/pointDrawChain';
import { solvePointDrawRoute, dominantAxisEnd } from '../../utils/pointDrawRoute';
import {
  routeHeadToSpokePort,
  projectOntoSpokeRay,
  isManhattanPolyline,
  isCardinalDeg,
  spokeSegHeading,
  normDeg,
  routeBetweenSpokePorts,
  routeCardinalHeadToDiagonalPort,
  rankSpokePorts,
} from '../../utils/pointDrawSpoke';
import { pairRebuildPieces, sessionRailsForRebuild } from '../../utils/pointDrawIdentity';
import { alignTrackToTravelHeading } from '../../utils/trackDirectionMarker';
import {
  computeToolboxDropPlacement,
  type ToolboxDropPlacement,
} from '../../utils/toolboxDropPlacement';
import {
  tryInsertOnRail,
  isCircleJunctionToolboxItem,
  insertRejectLabel,
  findPerpendicularStraightCrossing,
  planPerpendicularCircularJunctionInsert,
  planCircularJunctionInsertOnCurve,
  planPointDrawCurveJunctionInsert,
  findCurveTurnNear,
  findTrackNodePlacement,
  tryInsertTrackNodeOnRail,
  type InsertOnRailOptions,
} from '../../utils/railInsert';
import Grid from './Grid';
import FloorPlan from './FloorPlan';
import FloorPlanLabels from './FloorPlanLabels';
import SimulationFloorPlanImage2D from './SimulationFloorPlanImage2D';
import FloorPlanTooltip from './FloorPlanTooltip';
import FacilityZones from './FacilityZones';
import FacilityZoneTooltip from './FacilityZoneTooltip';
import MindmapHighlightOverlay from './MindmapHighlightOverlay';
import CanvasElement from './CanvasElement';
import PointDrawOverlay from './PointDrawOverlay';
import ElementContextMenu, { type ElementContextMenuState } from './ElementContextMenu';
import SimulationOverlay from './SimulationOverlay';
import SimulationInventoryOverlay from './SimulationInventoryOverlay';
import SimulationPickupReadyOverlay from './SimulationPickupReadyOverlay';
import SimulationSegmentHighlight from './SimulationSegmentHighlight';
import SimulationTrackNodeOverlay from './SimulationTrackNodeOverlay';
import { StartNode2DShape, EndNode2DShape } from './Amhs2dShapes';
import EquipmentOverlay from './EquipmentOverlay';
import EquipmentDrawOverlay from './EquipmentDrawOverlay';
import EquipmentContextMenu, { type EquipmentContextMenuState } from '../equipment/EquipmentContextMenu';
import EquipmentTooltip from '../equipment/EquipmentTooltip';
import {
  addDrawPoint,
  selectInstance,
  selectInstances,
  setEquipmentReviewSelection,
  setEquipmentVisible,
  setPendingPolygon,
} from '../../features/equipment/equipmentSlice';
import { isLayoutElementHiddenByEquipmentOverlay } from '../../utils/projectExport';
import {
  pointInPolygon,
  polygonBounds,
  polygonCentroid,
  rectPolygon,
  isValidEquipmentFootprint,
  resolvePorts,
  resolveStyle,
  equipmentProvenance,
  equipmentReviewStatus,
  EQUIPMENT_PROVENANCE_LABEL,
  isEquipmentOverlayVisible,
  worldPolygon,
} from '../../utils/equipmentGeometry';
import {
  computePolylineBBoxes,
  getRobustFitBounds,
  pickPolyline,
} from '../../utils/floorplanHitTest';
import { pickRegion } from '../../utils/facility';
import { computeNextEntityName } from '../../utils/entityNaming';
import {
  collectExistingEntityIds,
  computeNextEntityId,
  prefixForElement,
} from '../../utils/entityIds';
import Konva from 'konva';

type PendingCircleJunctionDrop = {
  item: ToolboxItem;
  worldPt: { x: number; y: number };
  uiPt: { x: number; y: number };
  portCount: string;
};

type PendingStockerDrop = {
  item: ToolboxItem;
  worldPt: { x: number; y: number };
  uiPt: { x: number; y: number };
  bayCount: string;
};

/** Keeps the drag ghost from re-rendering on mouse moves that don't move it. */
const isSamePlacement = (
  a: ToolboxDropPlacement | null,
  b: ToolboxDropPlacement
): boolean => {
  if (!a) return false;
  const pa = a.element;
  const pb = b.element;
  return (
    pa.type === pb.type &&
    pa.rotation === pb.rotation &&
    pa.position.x === pb.position.x &&
    pa.position.y === pb.position.y &&
    pa.dimensions.width === pb.dimensions.width &&
    pa.dimensions.height === pb.dimensions.height &&
    a.snapPoint?.x === b.snapPoint?.x &&
    a.snapPoint?.y === b.snapPoint?.y &&
    a.junctionStubs.length === b.junctionStubs.length
  );
};

const Canvas: React.FC = () => {
  const dispatch = useAppDispatch();
  const [viewport, setViewport] = useViewport();
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const [isPanning, setIsPanning] = useState(false);
  // Spacebar temporarily activates pan (Figma-style) without changing Redux tool state.
  const [spacebarPan, setSpacebarPan] = useState(false);
  const spacebarHeldRef = useRef(false);
  const [dragRailMeasure, setDragRailMeasure] = useState<{
    x: number;
    y: number;
    text: string;
    /** false = invalid placement feedback (amber). */
    valid?: boolean;
  } | null>(null);
  const [pendingCircleJunctionDrop, setPendingCircleJunctionDrop] =
    useState<PendingCircleJunctionDrop | null>(null);
  const [pendingStockerDrop, setPendingStockerDrop] =
    useState<PendingStockerDrop | null>(null);
  /** Toolbox item currently being dragged (Chrome blocks getData during dragover). */
  const draggingToolboxItemRef = useRef<ToolboxItem | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<Konva.Stage | null>(null);

  // Spacebar-hold pan: activate when space is pressed outside any text input,
  // deactivate the moment the key is released (or focus leaves the window).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) return;
      if (spacebarHeldRef.current) return; // already held — suppress key-repeat
      e.preventDefault();
      spacebarHeldRef.current = true;
      setSpacebarPan(true);
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;
      spacebarHeldRef.current = false;
      setSpacebarPan(false);
    };

    const onBlur = () => {
      // Safety: release pan if the window loses focus while space is held.
      spacebarHeldRef.current = false;
      setSpacebarPan(false);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  // Rubber-band selection state
  const [selectionRect, setSelectionRect] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const selectionStartRef = useRef<{ x: number; y: number } | null>(null);
  const selectionRectRef = useRef<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const isRubberBandingRef = useRef(false);
  /** Skip the click that follows a completed marquee so it doesn't clear the new selection. */
  const suppressNextStageClickRef = useRef(false);

  // Live preview position for a stop being placed (snapped onto the nearest rail).
  const [stopPreview, setStopPreview] = useState<{ x: number; y: number } | null>(null);
  const [trackNodePreview, setTrackNodePreview] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const elements = useAppSelector((state) => state.layout.elements);
  const elementOrder = useAppSelector((state) => state.layout.elementOrder);
  const layoutLayers = useAppSelector((state) => state.layout.layers);
  const layoutLayerOrder = useAppSelector((state) => state.layout.layerOrder);
  const selectedIds = useAppSelector((state) => state.selection.selectedIds);
  const zoomLevel = useAppSelector((state) => state.ui.zoomLevel);
  const showGrid = useAppSelector((state) => state.ui.showGrid);
  const gridSettings = useAppSelector((state) => state.ui.gridSettings);
  const activeTool = useAppSelector((state) => state.ui.activeTool);
  const leftSidebarOpen = useAppSelector((state) => state.ui.leftSidebarOpen);
  const rightSidebarOpen = useAppSelector((state) => state.ui.rightSidebarOpen);
  // Simulator is a read-only view of the layout, so it never resolves to an
  // editing tool: marquee select, locked-layer drag and resize all key off this.
  const simulationMode = useAppSelector((state) => state.ui.simulationPanelOpen);
  // While spacebar is held, behave as if 'pan' is active without touching Redux.
  const effectiveTool = spacebarPan || simulationMode ? 'pan' : activeTool;
  const simulationRunning = useAppSelector((state) => state.simulation.running);
  const view3dOpen = useAppSelector((state) => state.ui.view3dOpen);
  const view3dSceneRevealed = useAppSelector((state) => state.ui.view3dSceneRevealed);
  /** Unmount Konva only when 3D has taken over — not while the opening loader is up. */
  const suspendKonvaFor3d = view3dOpen && view3dSceneRevealed;
  const placingStopForVehicle = useAppSelector((state) => state.ui.placingStopForVehicle);
  const measurementUnit = useAppSelector((state) => state.ui.measurementUnit);
  const vehicleControls = useAppSelector((state) => state.simulation.vehicles);
  const pointDraw = useAppSelector((state) => state.ui.pointDraw);
  const trackNodePlacementActive = useAppSelector(
    (state) => state.ui.trackNodePlacementActive
  );
  const routeEndpointPlacement = useAppSelector((state) => state.ui.routeEndpointPlacement);
  const routeStartPoint = useAppSelector((state) => state.ui.routeStartPoint);
  const routeEndPoint = useAppSelector((state) => state.ui.routeEndPoint);

  // ── Equipment Identification tool ───────────────────────────────────────────
  const eqDraw = useAppSelector((state) => state.equipment.draw);
  const eqInstances = useAppSelector((state) => state.equipment.instances);
  const eqInstanceOrder = useAppSelector((state) => state.equipment.instanceOrder);
  const eqVisible = useAppSelector((state) => state.equipment.visible);
  const eqTemplates = useAppSelector((state) => state.equipment.templates);
  const eqHiddenProcesses = useAppSelector((state) => state.equipment.hiddenProcesses);
  const [eqCursor, setEqCursor] = useState<{ x: number; y: number } | null>(null);
  const [equipmentContextMenu, setEquipmentContextMenu] =
    useState<EquipmentContextMenuState | null>(null);
  const [equipmentHover, setEquipmentHover] = useState<{
    instanceId: string;
    sx: number;
    sy: number;
  } | null>(null);

  const handleEquipmentHover = useCallback(
    (hover: { instanceId: string; sx: number; sy: number }) => {
      setEquipmentHover(hover);
    },
    []
  );

  const handleEquipmentHoverEnd = useCallback(() => {
    setEquipmentHover((h) => (h ? null : h));
  }, []);

  const handleEquipmentContextMenu = useCallback(
    (coords: { clientX: number; clientY: number }, instanceId: string) => {
      if (wasRightClickPannedRef.current) return;
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setElementContextMenu(null);
      setEquipmentContextMenu({
        x: coords.clientX - rect.left,
        y: coords.clientY - rect.top,
        instanceId,
      });
    },
    []
  );

  /** Suppress the browser canvas menu; open equipment menu when a footprint is under the cursor. */
  const handleStageContextMenu = useCallback(
    (e: Konva.KonvaEventObject<PointerEvent>) => {
      e.evt.preventDefault();
      if (wasRightClickPannedRef.current) return;

      if (eqDraw.active || pointDraw.active || trackNodePlacementActive) return;

      // Pan mode: right-click reveals equipment overlays.
      if (effectiveTool === 'pan') {
        dispatch(setEquipmentVisible(true));
      }

      const stage = stageRef.current;
      const rect = containerRef.current?.getBoundingClientRect();
      if (!stage || !rect) return;

      const pointer = stage.getPointerPosition();
      if (!pointer) return;

      const world = screenToWorld(pointer, {
        stagePos: { x: viewport.panX, y: viewport.panY },
        stageScale: viewport.zoom,
      });
      // Top-most instance first (later in draw order).
      for (let i = eqInstanceOrder.length - 1; i >= 0; i -= 1) {
        const id = eqInstanceOrder[i];
        const inst = eqInstances[id];
        if (!inst) continue;
        if (!pointInPolygon(world.x, world.y, inst.polygon)) continue;

        if (!eqVisible) dispatch(setEquipmentVisible(true));
        dispatch(selectInstance(id));
        dispatch(clearSelection());
        setElementContextMenu(null);
        setEquipmentContextMenu({
          x: e.evt.clientX - rect.left,
          y: e.evt.clientY - rect.top,
          instanceId: id,
        });
        return;
      }
    },
    [
      eqDraw.active,
      pointDraw.active,
      trackNodePlacementActive,
      effectiveTool,
      eqInstanceOrder,
      eqInstances,
      eqVisible,
      viewport.panX,
      viewport.panY,
      viewport.zoom,
      dispatch,
    ]
  );

  // Equipment draw uses raw click points; the finished rectangle is rail-snapped on commit.
  const snapEquipmentPoint = useCallback((pt: { x: number; y: number }) => pt, []);

  // Handle a canvas click while the equipment drawing tool is active.
  // Returns true when the click was consumed by the tool.
  const handleEquipmentDrawClick = useCallback(
    (worldPt: { x: number; y: number }): boolean => {
      if (!eqDraw.active) return false;
      const pt = snapEquipmentPoint(worldPt);

      if (eqDraw.points.length === 0) {
        dispatch(addDrawPoint(pt));
      } else {
        const anchor = eqDraw.points[0];
        let polygon = rectPolygon(anchor, pt);
        let rotation = 0;
        if (isValidEquipmentFootprint(polygon)) {
          const layoutRails = Object.values(elements);
          ({ polygon, rotation } = snapEquipmentPolygonToNearestRail(
            polygon,
            rotation,
            layoutRails
          ));
          dispatch(
            setPendingPolygon({
              polygon,
              rotation,
            })
          );
        }
      }
      return true;
    },
    [eqDraw.active, eqDraw.points, dispatch, snapEquipmentPoint, elements]
  );

  // Live pointer position for the Point Draw rubber-band segment. Kept in local
  // state (like the equipment cursor) so per-frame mouse moves don't churn Redux.
  const [pointPreview, setPointPreview] = useState<{ x: number; y: number } | null>(null);

  // The most recently committed Point Draw straight, so a corner fillet can be
  // inserted (and the straight shortened by one radius) when the heading turns.
  const lastPointStraightRef = useRef<{
    id: string;
    heading: number; // 0=E, 90=S, 180=W, 270=N
    width: number; // straight length (world units)
    height: number; // straight thickness (world units)
  } | null>(null);

  // The first committed Point Draw straight of the current polyline, so an
  // intelligent loop closure can recompute the corner at the start point (by
  // shortening this straight's start and inserting a fixed-radius curve).
  const firstPointStraightRef = useRef<{ id: string; heading: number } | null>(null);

  // Element ids committed during the current Point Draw session, so endpoint
  // snapping ("continue from an existing endpoint") never latches onto the run
  // being drawn right now.
  const pointDrawSessionIdsRef = useRef<Set<string>>(new Set());

  // Rotary-junction port hints shown while Point Draw is active: every nearby
  // port ring, the currently snapped/active port, and the previewed tangent
  // route from the head to that port.
  const [portHints, setPortHints] = useState<{
    ports: { x: number; y: number; legal: boolean }[];
    activeTip: { x: number; y: number } | null;
    route: number[] | null;
  }>({ ports: [], activeTip: null, route: null });

  // Nearest existing-rail centerline point under the cursor while Point Draw is
  // active. Detected centrally by the controller (via projectPointToNearestRail)
  // rather than element hit events, and shown purely as guidance — placement
  // still snaps to the grid.
  const [pointDrawCenterline, setPointDrawCenterline] = useState<
    { x: number; y: number } | null
  >(null);

  // Ghost of the track currently being dragged in from the toolbox, resolved
  // through the same snap pipeline as the drop so it shows the real result.
  const [dropPreview, setDropPreview] = useState<ToolboxDropPlacement | null>(null);

  /** Abandon the current run while keeping Point Draw active, like first Escape. */
  const resetPointDrawRun = useCallback(() => {
    lastPointStraightRef.current = null;
    firstPointStraightRef.current = null;
    pointDrawSessionIdsRef.current.clear();
    dispatch(clearPointDraw());
  }, [dispatch]);

  // Drop the placement preview as soon as placement mode is exited.
  useEffect(() => {
    if (!placingStopForVehicle) setStopPreview(null);
  }, [placingStopForVehicle]);

  const validation = useAppSelector((state) => state.validation);
  const floorplanBounds = useAppSelector((state) => state.floorplan.bounds);

  // Floor-plan geometry for hover hit-testing / inspection popover.
  const fpPolylines = useAppSelector((state) => state.floorplan.polylines);
  const fpPolylineLayers = useAppSelector((state) => state.floorplan.polylineLayers);
  const fpPolylineMeta = useAppSelector((state) => state.floorplan.polylineMeta);
  const fpLayers = useAppSelector((state) => state.floorplan.layers);
  const fpHiddenEntityTypes = useAppSelector((state) => state.floorplan.hiddenEntityTypes);
  const fpVisible = useAppSelector((state) => state.floorplan.visible);
  const fpScaleFactor = useAppSelector((state) => state.floorplan.scaleFactor);
  const dxfUnit = useAppSelector((state) => state.floorplan.dxfUnit);
  const dxfUnitsPerMeter = useAppSelector((state) => state.floorplan.dxfUnitsPerMeter);
  const fpCalibration = useAppSelector((state) => state.floorplan.calibration);
  const fpMmPerCanvasUnit = useAppSelector((state) => state.floorplan.millimetersPerCanvasUnit);
  const floorPlanScaleFactor = fpScaleFactor;
  const hasFloorPlanCalibration = shouldUseDxfCalibration(
    Boolean(floorplanBounds),
    fpPolylines.length
  );
  const effectiveScaleFactor = hasFloorPlanCalibration ? floorPlanScaleFactor : undefined;
  const effectiveDxfUnitsPerMeter = hasFloorPlanCalibration ? dxfUnitsPerMeter : undefined;

  // One bbox-per-polyline index, rebuilt only when the geometry changes.
  const fpBBoxes = useMemo(() => computePolylineBBoxes(fpPolylines), [fpPolylines]);

  // Facility zones for hover/selection.
  const facilityRegions = useAppSelector((state) => state.facility.regions);
  const facilityVisible = useAppSelector((state) => state.facility.visible);
  const facilityVisibleTypes = useAppSelector((state) => state.facility.visibleTypes);
  const facilityMinConfidence = useAppSelector((state) => state.facility.minConfidence);
  const facilitySelectedId = useAppSelector((state) => state.facility.selectedId);
  const vehicles = useAppSelector((state) => state.simulation.vehicles);
  const stops = useMemo(() => {
    return Object.values(vehicles).flatMap((v) => v.stops || []);
  }, [vehicles]);

  // Which floor-plan polyline the pointer is currently over (and where on screen).
  const [hover, setHover] = useState<{ index: number; sx: number; sy: number } | null>(null);
  // Which facility zone the pointer is over (takes priority over polyline hover).
  const [zoneHover, setZoneHover] = useState<{ id: string; sx: number; sy: number } | null>(null);
  const [hoveredStop, setHoveredStop] = useState<string | null>(null);
  const hoverRafRef = useRef<number | null>(null);
  const pendingPointerRef = useRef<{ sx: number; sy: number } | null>(null);
  const layerDragStartPositionsRef = useRef<Record<string, { x: number; y: number }>>({});
  const layerDragSnapshotRef = useRef<Record<string, LayoutElement>>({});
  const [activeTransformWrapper, setActiveTransformWrapper] = useState<{
    id: string;
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
  } | null>(null);
  const [selectedLockedLayerId, setSelectedLockedLayerId] = useState<string | null>(null);
  const [elementContextMenu, setElementContextMenu] = useState<ElementContextMenuState | null>(null);
  const wrapperRefs = useRef<Record<string, Konva.Group>>({});
  const layerTransformerRef = useRef<Konva.Transformer>(null);

  const isRightClickPanningRef = useRef(false);
  const wasRightClickPannedRef = useRef(false);
  const rightClickPanStartRef = useRef<{ clientX: number; clientY: number; panX: number; panY: number } | null>(null);
  const pendingRightPanRef = useRef<{ panX: number; panY: number } | null>(null);
  const rightPanRafRef = useRef<number | null>(null);
  const [isRightClickPanningState, setIsRightClickPanningState] = useState(false);
  const viewportGestureRef = useRef(false);
  const [viewportGestureActive, setViewportGestureActive] = useState(false);

  useEffect(() => {
    const handleWindowPointerMove = (e: PointerEvent) => {
      if (!isRightClickPanningRef.current || !rightClickPanStartRef.current) return;

      const dx = e.clientX - rightClickPanStartRef.current.clientX;
      const dy = e.clientY - rightClickPanStartRef.current.clientY;

      if (!wasRightClickPannedRef.current && Math.hypot(dx, dy) > 3) {
        wasRightClickPannedRef.current = true;
        setIsRightClickPanningState(true);
        viewportGestureRef.current = true;
        setViewportGestureActive(true);
      }

      if (wasRightClickPannedRef.current) {
        pendingRightPanRef.current = {
          panX: rightClickPanStartRef.current.panX + dx,
          panY: rightClickPanStartRef.current.panY + dy,
        };
        if (rightPanRafRef.current == null) {
          rightPanRafRef.current = requestAnimationFrame(() => {
            rightPanRafRef.current = null;
            const next = pendingRightPanRef.current;
            const stage = stageRef.current;
            if (!next || !stage) return;
            stage.position({ x: next.panX, y: next.panY });
            stage.batchDraw();
          });
        }
      }
    };

    const handleWindowPointerUp = (e: PointerEvent) => {
      if (e.button === 2 || isRightClickPanningRef.current) {
        const finalPan = pendingRightPanRef.current;
        if (finalPan) setViewport(finalPan);
        pendingRightPanRef.current = null;
        viewportGestureRef.current = false;
        setViewportGestureActive(false);
        isRightClickPanningRef.current = false;
        rightClickPanStartRef.current = null;
        setIsRightClickPanningState(false);
        setTimeout(() => {
          wasRightClickPannedRef.current = false;
        }, 50);
      }
    };

    window.addEventListener('pointermove', handleWindowPointerMove);
    window.addEventListener('pointerup', handleWindowPointerUp);
    return () => {
      window.removeEventListener('pointermove', handleWindowPointerMove);
      window.removeEventListener('pointerup', handleWindowPointerUp);
      if (rightPanRafRef.current != null) cancelAnimationFrame(rightPanRafRef.current);
    };
  }, [setViewport]);

  const handleContainerPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button === 2) {
        isRightClickPanningRef.current = true;
        wasRightClickPannedRef.current = false;
        rightClickPanStartRef.current = {
          clientX: e.clientX,
          clientY: e.clientY,
          panX: viewport.panX,
          panY: viewport.panY,
        };
      }
    },
    [viewport.panX, viewport.panY]
  );

  useEffect(() => {
    if (selectedLockedLayerId && wrapperRefs.current[selectedLockedLayerId] && layerTransformerRef.current) {
      layerTransformerRef.current.nodes([wrapperRefs.current[selectedLockedLayerId]]);
      layerTransformerRef.current.getLayer()?.batchDraw();
    }
  }, [selectedLockedLayerId]);

  const gridUnits = GRID_SIZE;

  // Effective scale: DXF-derived when a floor plan is present; otherwise fallback.
  const calibration = getEffectiveCalibration(effectiveScaleFactor, effectiveDxfUnitsPerMeter);
  const metersPerUnit = calibration.metersPerUnit;
  const millimetersPerUnit = calibration.millimetersPerUnit;
  const unitsPerMeter = calibration.unitsPerMeter;

  // Screen space (Konva scale applies world units -> CSS pixels)
  const pixelsPerUnit = zoomLevel;
  const metersPerGrid = gridUnits * metersPerUnit;
  const gridsPerMeter = metersPerGrid > 0 ? 1 / metersPerGrid : 0;
  const pixelsPerGrid = gridUnits * pixelsPerUnit;
  const pixelsPerMeter = pixelsPerUnit * unitsPerMeter;
  const metersPerPixel = pixelsPerMeter > 0 ? 1 / pixelsPerMeter : 0;

  const unitPerWorldLabel = useMemo(() => {
    if (dxfUnit === 'm') return `${metersPerUnit.toFixed(4)} m`;
    if (dxfUnit === 'ft') return `${(metersPerUnit / 0.3048).toFixed(4)} ft`;
    if (dxfUnit === 'in') return `${(metersPerUnit / 0.0254).toFixed(4)} in`;
    if (dxfUnit === 'cm') return `${(metersPerUnit * 100).toFixed(2)} cm`;
    return `${millimetersPerUnit.toFixed(2)} mm`;
  }, [dxfUnit, metersPerUnit, millimetersPerUnit]);

  const setZoomIfChanged = useCallback(
    (nextZoom: number) => {
      if (Math.abs(nextZoom - zoomLevel) > 1e-9) {
        dispatch(setZoomLevel(nextZoom));
      }
    },
    [dispatch, zoomLevel]
  );

  // Debounce Redux zoom during wheel so CanvasElement / EquipmentOverlay don't
  // re-render on every tick — viewportStore remains the hot path for Stage.
  const zoomReduxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wheelRafRef = useRef<number | null>(null);
  const wheelSettleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingWheelViewportRef = useRef<{
    zoom: number;
    panX: number;
    panY: number;
  } | null>(null);
  const scheduleZoomReduxSync = useCallback(
    (nextZoom: number) => {
      if (zoomReduxTimerRef.current) clearTimeout(zoomReduxTimerRef.current);
      zoomReduxTimerRef.current = setTimeout(() => {
        zoomReduxTimerRef.current = null;
        dispatch(setZoomLevel(nextZoom));
      }, 120);
    },
    [dispatch]
  );
  useEffect(
    () => () => {
      if (zoomReduxTimerRef.current) clearTimeout(zoomReduxTimerRef.current);
      if (wheelSettleTimerRef.current) clearTimeout(wheelSettleTimerRef.current);
      if (wheelRafRef.current != null) cancelAnimationFrame(wheelRafRef.current);
    },
    []
  );

  // Names issued since the last store update. A single action can build several
  // elements of one type (splitting a rail around a junction, a Point Draw run),
  // and those siblings are not in `elements` yet, so reserving keeps them unique.
  const reservedNamesRef = useRef<Set<string>>(new Set());
  const reservedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    reservedNamesRef.current.clear();
    reservedIdsRef.current.clear();
  }, [elements, eqInstances]);

  const getNextEntityName = useCallback(
    (type: ElementType) => {
      const name = computeNextEntityName(
        type,
        Object.values(elements),
        reservedNamesRef.current
      );
      reservedNamesRef.current.add(name.trim().toLowerCase());
      return name;
    },
    [elements]
  );

  const getNextEntityId = useCallback(
    (type: ElementType, railType?: string | null) => {
      const id = computeNextEntityId(
        prefixForElement(type, railType),
        collectExistingEntityIds(Object.values(elements), Object.values(eqInstances)),
        reservedIdsRef.current
      );
      reservedIdsRef.current.add(id);
      return id;
    },
    [elements, eqInstances]
  );

  // Default segment sizing for Point Draw, mirroring the toolbox track SKUs
  // (3 m straight, DEFAULT_TRACK_THICKNESS_METERS, DEFAULT_CURVE_RADIUS_METERS)
  // so drawn rails match drag-dropped ones.
  const segmentSizing = useMemo(
    () => ({
      lengthUnits: Math.max(
        1,
        metersToEffectiveWorldUnits(3, effectiveScaleFactor, effectiveDxfUnitsPerMeter)
      ),
      thicknessUnits: Math.max(
        0.001,
        metersToEffectiveWorldUnits(
          DEFAULT_TRACK_THICKNESS_METERS,
          effectiveScaleFactor,
          effectiveDxfUnitsPerMeter
        )
      ),
      radiusUnits: Math.max(
        0.001,
        metersToEffectiveWorldUnits(
          DEFAULT_CURVE_RADIUS_METERS,
          effectiveScaleFactor,
          effectiveDxfUnitsPerMeter
        )
      ),
    }),
    [effectiveScaleFactor, effectiveDxfUnitsPerMeter]
  );

  // Point Draw always snaps to the snap lattice (PCB-routing style): every clicked
  // point and the live preview lock to the nearest snap intersection,
  // regardless of the global snap-to-grid toggle. Snapping happens here, before
  // any segment geometry is generated, so points can never land off-lattice.
  const snapPointDrawPoint = useCallback(
    (pt: { x: number; y: number }) => snapPositionToGrid(pt, gridSettings.snapGridSize),
    [gridSettings.snapGridSize]
  );

  // Build a straight `overhead-rail` LayoutElement between two world points.
  // Produces the same kind of element as drag-and-drop placement, so it
  // renders, selects, edits, validates, and undoes identically.
  const buildPointStraight = useCallback(
    (from: { x: number; y: number }, to: { x: number; y: number }) => {
      const seg = computeSegmentBetween(from, to, segmentSizing);
      const layerId = DEFAULT_LAYER_FOR_TYPE['overhead-rail'] ?? 'layer-1';
      const lengthMeters = worldUnitsToEffectiveMeters(
        seg.dimensions.width,
        effectiveScaleFactor,
        effectiveDxfUnitsPerMeter
      );

      const element: LayoutElement = alignTrackToTravelHeading(
        {
          id: getNextEntityId('overhead-rail', 'straight'),
          type: 'overhead-rail',
          layerId,
          position: seg.position,
          rotation: seg.rotation,
          dimensions: seg.dimensions,
          metadata: {
            name: getNextEntityName('overhead-rail'),
            locked: false,
            visible: true,
            color: ELEMENT_COLORS['overhead-rail'],
            layerId,
          },
          customProperties: {
            sku: 'AeroTrak',
            railType: 'straight',
            direction: DEFAULT_TRACK_DIRECTION,
            heightM: 3.2,
            thicknessMeters: DEFAULT_TRACK_THICKNESS_METERS,
            lengthMeters: lengthMeters > 0 ? lengthMeters : 3,
          },
        },
        seg.travelHeadingDeg ?? seg.rotation
      );

      return { element, seg };
    },
    [segmentSizing, getNextEntityName, getNextEntityId, effectiveScaleFactor, effectiveDxfUnitsPerMeter]
  );

  // ── Point-Draw constraint-solving generator ─────────────────────────────────
  //
  // Delegate every path build to the shared manufactured-track invariant.
  // The builder either returns fixed-radius Curve90 corners at every direction
  // change or rejects the entire path without returning partial geometry.
  const buildChainPieces = useCallback(
    (
      vertices: { x: number; y: number }[],
      R: number,
      opts?: { closed?: boolean }
    ) => {
      const result = buildOrthogonalTrackPath(
        vertices,
        { ...segmentSizing, radiusUnits: R },
        { closed: opts?.closed, minStraightUnits: R }
      );
      return result.ok ? result.pieces : [];
    },
    [segmentSizing]
  );

  // Build a real LayoutElement from one regenerated geometry piece. Straights
  // stay `overhead-rail`; curves use `overhead-curve` so they match toolbox drop.
  const segToPointElement = useCallback(
    (
      seg: ReturnType<typeof computeSegmentBetween>,
      reuse?: LayoutElement
    ): LayoutElement => {
      const isStraight = seg.railType === 'straight';
      const elementType: ElementType = isStraight ? 'overhead-rail' : 'overhead-curve';
      const layerId = reuse?.layerId ?? DEFAULT_LAYER_FOR_TYPE[elementType] ?? 'layer-1';
      const lengthMeters = worldUnitsToEffectiveMeters(
        seg.dimensions.width,
        effectiveScaleFactor,
        effectiveDxfUnitsPerMeter
      );
      const direction =
        typeof reuse?.customProperties?.direction === 'string'
          ? reuse.customProperties.direction
          : DEFAULT_TRACK_DIRECTION;
      const curveSku =
        seg.railType === 'curve45'
          ? 'AeroTrak AT3'
          : seg.railType === 'curve180'
            ? 'AeroTrak AT4'
            : 'AeroTrak AT2';
      const element: LayoutElement = {
        id: reuse?.id ?? getNextEntityId(elementType, isStraight ? 'straight' : seg.railType),
        type: elementType,
        layerId,
        position: seg.position,
        rotation: seg.rotation,
        dimensions: seg.dimensions,
        metadata: {
          name: reuse?.metadata.name ?? getNextEntityName(elementType),
          locked: reuse?.metadata.locked ?? false,
          visible: reuse?.metadata.visible ?? true,
          color: reuse?.metadata.color ?? ELEMENT_COLORS[elementType],
          layerId,
          ...(reuse?.metadata.groupId ? { groupId: reuse.metadata.groupId } : {}),
        },
        customProperties: isStraight
          ? {
              sku: 'AeroTrak',
              railType: 'straight',
              direction,
              heightM: 3.2,
              thicknessMeters: DEFAULT_TRACK_THICKNESS_METERS,
              lengthMeters: lengthMeters > 0 ? lengthMeters : 3,
            }
          : {
              sku: curveSku,
              railType: seg.railType,
              direction,
              heightM: 3.2,
              thicknessMeters: DEFAULT_TRACK_THICKNESS_METERS,
              radiusMeters: DEFAULT_CURVE_RADIUS_METERS,
              // curveSweepSign is only meaningful for Curve180; Curve90 direction
              // is encoded in position/rotation. Writing it onto Curve90 breaks CCW turns.
              ...(seg.railType === 'curve180' && seg.curveSweepSign != null
                ? { curveSweepSign: seg.curveSweepSign }
                : {}),
            },
      };
      // Straights may be canonicalized to 0°/90°; reverse when that opposes draw order.
      return isStraight && seg.travelHeadingDeg != null
        ? alignTrackToTravelHeading(element, seg.travelHeadingDeg)
        : element;
    },
    [effectiveScaleFactor, effectiveDxfUnitsPerMeter, getNextEntityName, getNextEntityId]
  );

  // Pure feasibility pass for `regenerateRun`: decides whether the run can be
  // rebuilt and returns the geometry, without touching the store. The preview
  // uses this so a placement the commit would refuse never renders as legal.
  //
  // A run may open with a diagonal spoke leg off a circular-junction port. That
  // leg is not expressible as a Manhattan polyline, so it is kept verbatim and
  // only the axis-aligned tail after it is rebuilt — otherwise the first turn
  // anywhere later in the run would be rejected.
  const planRunRebuild = useCallback(
    (fullVertices: { x: number; y: number }[], closed: boolean) => {
      const leadingSpokeLeg =
        !closed &&
        fullVertices.length >= 3 &&
        !isAxisAligned(fullVertices[0], fullVertices[1]);
      const tailStart = leadingSpokeLeg ? 1 : 0;

      const clean = cleanupManhattanPolyline(fullVertices.slice(tailStart), closed);
      if (!clean.valid) return null;

      const pieces = buildChainPieces(clean.vertices, segmentSizing.radiusUnits, { closed });
      if (pieces.length === 0) return null;

      return { leadingSpokeLeg, clean, pieces };
    },
    [buildChainPieces, segmentSizing.radiusUnits]
  );

  // Rebuild the in-progress Point-Draw run from a corner-vertex polyline.
  //
  // This is the generalized connection/closure solver: the caller supplies the
  // full polyline (the run's committed corners plus any connection/closure
  // waypoints). We clean it into a legal Manhattan path/loop, regenerate every
  // primitive through the shared legal generator, and atomically swap the run's
  // rails for the result in one undoable step. Existing session rails keep their
  // TRACK / Rail identities; only extra pieces take a new number. Because the
  // run is rebuilt as one constrained graph, the junction between the drawn run
  // and the new connection is solved too (straights extend/shorten, corners
  // recompute, curves are replaced) — never a raw endpoint weld or an illegal
  // tangent. Returns false (mutating nothing) when no legal layout exists, so
  // the caller can reject the connection instead of drawing invalid geometry.
  const regenerateRun = useCallback(
    (fullVertices: { x: number; y: number }[], closed: boolean): boolean => {
      const plan = planRunRebuild(fullVertices, closed);
      if (!plan) return false;
      const { leadingSpokeLeg, clean, pieces } = plan;

      // The preserved spoke leg was the first rail committed for this run, so it
      // stays out of the replace set and is re-attached to the session below.
      const sessionIds = Array.from(pointDrawSessionIdsRef.current);
      const keptIds = leadingSpokeLeg ? sessionIds.slice(0, 1) : [];
      const oldIds = leadingSpokeLeg ? sessionIds.slice(1) : sessionIds;
      const oldElements = sessionRailsForRebuild(sessionIds, elements, leadingSpokeLeg ? 1 : 0);
      const newElements = pairRebuildPieces(pieces, oldElements).map(({ piece, reuse }) =>
        segToPointElement(piece, reuse)
      );

      dispatch({ type: removeElements.type, payload: oldIds, meta: { skipHistory: true } });
      dispatch({ type: addElements.type, payload: newElements, meta: { skipHistory: true } });
      dispatch(
        addHistoryAction({
          id: generateId(),
          type: 'replace',
          timestamp: Date.now(),
          elementIds: newElements.map((el) => el.id),
          previousState: oldElements,
          newState: newElements,
        })
      );
      pointDrawSessionIdsRef.current = new Set([
        ...keptIds,
        ...newElements.map((el) => el.id),
      ]);

      // Track the last straight for the next incremental fillet / U-turn decision.
      // Travel heading comes from the polyline (draw direction), not from the
      // stored element — straights are canonicalized to 0°/90° regardless of
      // whether the user drew left→right or right→left.
      const lastStraight = [...newElements].reverse().find((el) => resolveRailType(el) === 'straight');
      if (lastStraight) {
        const verts = clean.vertices;
        const heading =
          verts.length >= 2
            ? closed
              ? segHeading(verts[verts.length - 1], verts[0])
              : segHeading(verts[verts.length - 2], verts[verts.length - 1])
            : lastStraight.rotation || 0;
        lastPointStraightRef.current = {
          id: lastStraight.id,
          heading,
          width: lastStraight.dimensions.width,
          height: lastStraight.dimensions.height,
        };
        if (!firstPointStraightRef.current) {
          firstPointStraightRef.current = {
            id: lastStraight.id,
            heading,
          };
        }
      }

      return true;
    },
    [planRunRebuild, segToPointElement, elements, dispatch]
  );

  // World-space ports of a rotary (circle) junction: the spoke tip plus the
  // outward direction (from the junction centre through the tip).
  const getRotaryPorts = useCallback((el: LayoutElement) => {
    const w = el.dimensions.width;
    const h = el.dimensions.height;
    const rot = ((el.rotation || 0) * Math.PI) / 180;
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);
    const center = {
      x: el.position.x + (w / 2) * cos - (h / 2) * sin,
      y: el.position.y + (w / 2) * sin + (h / 2) * cos,
    };
    return getRailEndpoints(el).map((tip) => ({
      tip,
      outwardDeg: (Math.atan2(tip.y - center.y, tip.x - center.x) * 180) / Math.PI,
    }));
  }, []);

  /** If `pt` sits on a circular-junction spoke tip, return that spoke's outward deg. */
  const findJunctionSpokeAt = useCallback(
    (pt: { x: number; y: number }): number | null => {
      const tipTol = ENDPOINT_TOLERANCE;
      for (const el of Object.values(elements)) {
        if (!el.metadata.visible || resolveRailType(el) !== 'junction-circle') continue;
        for (const p of getRotaryPorts(el)) {
          if (Math.hypot(p.tip.x - pt.x, p.tip.y - pt.y) <= tipTol) {
            return normDeg(p.outwardDeg);
          }
        }
      }
      return null;
    },
    [elements, getRotaryPorts]
  );

  // Commit one Point Draw click via the pattern-based route solver (straight,
  // turn90, Curve180, or composed U-turn of Curve90 + Straight + Curve90).
  // When the head sits on a circular-junction port, the first leg follows the
  // spoke (including diagonals). After a diagonal spoke leg, the next click may
  // continue on a cardinal axis with a sharp corner (no 90° fillet).
  const commitPointDrawClick = useCallback(
    (
      cur: { x: number; y: number },
      target: { x: number; y: number },
      committedVertices: { x: number; y: number }[]
    ): { x: number; y: number }[] | null => {
      const prev = lastPointStraightRef.current;
      let travelHeading =
        prev?.heading ??
        (committedVertices.length >= 2
          ? spokeSegHeading(
              committedVertices[committedVertices.length - 2],
              committedVertices[committedVertices.length - 1]
            )
          : null);

      // First leg leaving a junction port: lock onto the spoke ray.
      if (travelHeading == null && committedVertices.length === 1) {
        const spokeOut = findJunctionSpokeAt(cur);
        if (spokeOut != null) {
          const end = projectOntoSpokeRay(cur, spokeOut, target);
          if (!end || Math.hypot(end.x - cur.x, end.y - cur.y) < 1e-6) return null;
          const { element, seg } = buildPointStraight(cur, end);
          pointDrawSessionIdsRef.current.add(element.id);
          dispatch(addElement(element));
          const heading = spokeSegHeading(cur, end);
          lastPointStraightRef.current = {
            id: element.id,
            heading,
            width: seg.dimensions.width,
            height: seg.dimensions.height,
          };
          if (!firstPointStraightRef.current) {
            firstPointStraightRef.current = { id: element.id, heading };
          }
          return [end];
        }
      }

      // After a non-cardinal spoke leg: allow a sharp cardinal continuation.
      if (travelHeading != null && !isCardinalDeg(travelHeading)) {
        const { end, heading } = dominantAxisEnd(cur, target);
        if (Math.hypot(end.x - cur.x, end.y - cur.y) < 1e-6) return null;
        const { element, seg } = buildPointStraight(cur, end);
        pointDrawSessionIdsRef.current.add(element.id);
        dispatch(addElement(element));
        lastPointStraightRef.current = {
          id: element.id,
          heading,
          width: seg.dimensions.width,
          height: seg.dimensions.height,
        };
        return [end];
      }

      const inboundStraightLength =
        prev?.width ??
        (committedVertices.length >= 2
          ? Math.hypot(
              committedVertices[committedVertices.length - 1].x -
                committedVertices[committedVertices.length - 2].x,
              committedVertices[committedVertices.length - 1].y -
                committedVertices[committedVertices.length - 2].y
            )
          : null);

      const R = segmentSizing.radiusUnits;
      const solution = solvePointDrawRoute(
        {
          head: cur,
          travelHeading,
          cursor: target,
          radius: R,
          minStraight: R,
          inboundStraightLength,
        },
        segmentSizing
      );
      if (!solution.valid || solution.appendedVertices.length === 0) return null;

      // Every direction change is rebuilt through the central path invariant.
      // Incremental updates are reserved for collinear straight continuation;
      // a turn must never fall through to a bare perpendicular straight.
      if (solution.pattern !== 'straight' || solution.appendedVertices.length > 1) {
        const full = [...committedVertices, ...solution.appendedVertices];
        if (!regenerateRun(full, false)) return null;
        return solution.appendedVertices;
      }

      // Incremental collinear straight.
      const end = solution.endPoint;
      const newHeading = solution.exitHeading;
      const { element, seg } = buildPointStraight(cur, end);
      pointDrawSessionIdsRef.current.add(element.id);
      dispatch(addElement(element));
      lastPointStraightRef.current = {
        id: element.id,
        heading: newHeading,
        width: seg.dimensions.width,
        height: seg.dimensions.height,
      };

      if (!firstPointStraightRef.current && lastPointStraightRef.current) {
        firstPointStraightRef.current = {
          id: lastPointStraightRef.current.id,
          heading: lastPointStraightRef.current.heading,
        };
      }

      return solution.appendedVertices;
    },
    [
      dispatch,
      segmentSizing,
      buildPointStraight,
      regenerateRun,
      findJunctionSpokeAt,
    ]
  );

  /**
   * Replaces one perpendicular straight/straight meeting with a Circular
   * Junction. Returns:
   * - 'committed' when insertion succeeded
   * - 'rejected' when a perpendicular occupied straight was found but the
   *   junction cannot legally fit (do not fall through to stacking)
   * - 'not-applicable' when ordinary Point Draw should continue
   */
  const commitPointDrawCrossing = useCallback(
    (
      cur: { x: number; y: number },
      target: { x: number; y: number },
      committedVertices: { x: number; y: number }[]
    ): 'committed' | 'rejected' | 'not-applicable' => {
      const prev = lastPointStraightRef.current;
      const travelHeading =
        prev?.heading ??
        (committedVertices.length >= 2
          ? segHeading(
              committedVertices[committedVertices.length - 2],
              committedVertices[committedVertices.length - 1]
            )
          : null);

      // Lock to the inbound travel axis when known; otherwise dominant axis
      // toward the click. Grid snap often lands short of a DXF centerline, so
      // crossing search pulls to the host within a small tolerance.
      let rayEnd = dominantAxisEnd(cur, target).end;
      if (travelHeading === 0 || travelHeading === 180) {
        if (Math.abs(target.x - cur.x) < 1e-6) return 'not-applicable';
        rayEnd = { x: target.x, y: cur.y };
      } else if (travelHeading === 90 || travelHeading === 270) {
        if (Math.abs(target.y - cur.y) < 1e-6) return 'not-applicable';
        rayEnd = { x: cur.x, y: target.y };
      }
      if (Math.hypot(rayEnd.x - cur.x, rayEnd.y - cur.y) < 1e-6) {
        return 'not-applicable';
      }

      const pullTolerance = Math.max(
        gridSettings.snapGridSize * 1.5,
        28 / Math.max(viewport.zoom, 1e-6)
      );
      const sessionIds = pointDrawSessionIdsRef.current;
      const crossing = findPerpendicularStraightCrossing(
        cur,
        rayEnd,
        Object.values(elements),
        sessionIds,
        { pullTolerance }
      );
      if (!crossing) return 'not-applicable';

      // Short-of-host snaps plan as a T onto the intersection; clicks that
      // already reach/pass the host keep the ray end (full cross when long enough).
      const reachedHost =
        Math.hypot(rayEnd.x - cur.x, rayEnd.y - cur.y) + 1e-6 >=
        Math.hypot(
          crossing.intersection.x - cur.x,
          crossing.intersection.y - cur.y
        );
      const planEnd = reachedHost ? rayEnd : crossing.intersection;

      const plan = planPerpendicularCircularJunctionInsert(
        crossing.host,
        cur,
        planEnd,
        Object.values(elements),
        {
          nextName: (type) => getNextEntityName(type),
          nextId: (type, railType) => getNextEntityId(type, railType),
          lengthMetersForWidth: (width) =>
            worldUnitsToEffectiveMeters(
              width,
              effectiveScaleFactor,
              effectiveDxfUnitsPerMeter
            ),
        }
      );
      if (!plan.ok) return 'rejected';

      const { element: entryRail } = buildPointStraight(cur, plan.entryPort);
      const newElements = [...plan.addElements, entryRail];

      if (plan.continuesBeyond) {
        const built = buildPointStraight(plan.exitPort, planEnd);
        newElements.push(built.element);
      }

      dispatch({
        type: removeElements.type,
        payload: plan.removeIds,
        meta: { skipHistory: true },
      });
      dispatch({
        type: addElements.type,
        payload: newElements,
        meta: { skipHistory: true },
      });
      dispatch(
        addHistoryAction({
          id: generateId(),
          type: 'replace',
          timestamp: Date.now(),
          elementIds: newElements.map((element) => element.id),
          previousState: [crossing.host],
          newState: newElements,
        })
      );

      // Junction creation finishes this run. Point Draw remains active so the
      // next click can start independently from any available port or point.
      resetPointDrawRun();
      return 'committed';
    },
    [
      buildPointStraight,
      dispatch,
      effectiveDxfUnitsPerMeter,
      effectiveScaleFactor,
      elements,
      getNextEntityName,
      getNextEntityId,
      gridSettings.snapGridSize,
      resetPointDrawRun,
      viewport.zoom,
    ]
  );

  // Rebuild the run as an open lead-in tail plus a closed loop, joined by a
  // Circular Junction at the corner where the two meet.
  //
  // A loop is often reached by branching off existing track, which spends the
  // run's opening vertices on a lead-in stub. Closing such a run leaves three
  // rails converging on the anchor, and a T is only legal here as a junction —
  // welding the stub onto the loop wall produces geometry the rest of the
  // pipeline rejects. Handing the whole polyline to the cleanup instead is worse:
  // it reads the stub as a 180° reversal spike and silently deletes it, closing
  // the loop but cutting it loose from the track it was branched from.
  //
  // The junction is planned against the freshly built loop rather than against
  // the store, so the split, the junction and the shortened stub land in a single
  // undoable step and no intermediate illegal state is ever dispatched.
  //
  // A tail whose only leg is a diagonal junction spoke is not supported: it has
  // no Manhattan form, and the perpendicular junction planner needs an
  // axis-aligned approach. Such a closure is refused rather than approximated.
  const commitLoopWithLeadIn = useCallback(
    (
      tailVertices: { x: number; y: number }[],
      loopVertices: { x: number; y: number }[]
    ): boolean => {
      const anchor = tailVertices[tailVertices.length - 1];
      const approach = tailVertices[tailVertices.length - 2];
      if (!anchor || !approach) return false;

      const loopPlan = planRunRebuild(loopVertices, true);
      if (!loopPlan) return false;
      const sessionIds = Array.from(pointDrawSessionIdsRef.current);
      const skipLeadingSpoke =
        tailVertices.length >= 2 && !isAxisAligned(tailVertices[0], tailVertices[1]);
      const reusable = sessionRailsForRebuild(
        sessionIds,
        elements,
        skipLeadingSpoke ? 1 : 0
      );
      const loopElements = pairRebuildPieces(loopPlan.pieces, reusable).map(({ piece, reuse }) =>
        segToPointElement(piece, reuse)
      );

      const insertOptions: InsertOnRailOptions = {
        nextName: (type) => getNextEntityName(type),
        nextId: (type, railType) => getNextEntityId(type, railType),
        lengthMetersForWidth: (width) =>
          worldUnitsToEffectiveMeters(
            width,
            effectiveScaleFactor,
            effectiveDxfUnitsPerMeter
          ),
      };

      // The anchor lies mid-span on exactly one loop straight. The rest are
      // either parallel to the stub or nowhere near it, and the planner rejects
      // those on its own, so trying each in turn needs no separate hit test.
      const pool = [...Object.values(elements), ...loopElements];
      let plan: ReturnType<typeof planPerpendicularCircularJunctionInsert> | null = null;
      for (const host of loopElements) {
        if (resolveRailType(host) !== 'straight') continue;
        const attempt = planPerpendicularCircularJunctionInsert(
          host,
          approach,
          anchor,
          pool,
          insertOptions
        );
        if (attempt.ok) {
          plan = attempt;
          break;
        }
      }
      if (!plan?.ok) return false;

      // The stub now stops at the junction's spoke tip rather than running all
      // the way to the loop centerline it previously ended on.
      const tailPlan = planRunRebuild(
        [...tailVertices.slice(0, -1), plan.entryPort],
        false
      );
      if (!tailPlan) return false;
      const tailElements = pairRebuildPieces(
        tailPlan.pieces,
        reusable.slice(loopPlan.pieces.length)
      ).map(({ piece, reuse }) => segToPointElement(piece, reuse));

      // plan.removeIds names the loop straight the junction split. That rail only
      // ever existed inside this function, so it is dropped from the add set
      // instead of being dispatched as a removal.
      const newElements = [
        ...loopElements.filter((el) => !plan.removeIds.includes(el.id)),
        ...plan.addElements,
        ...tailElements,
      ];

      const oldIds = tailPlan.leadingSpokeLeg ? sessionIds.slice(1) : sessionIds;
      const oldElements = sessionRailsForRebuild(
        sessionIds,
        elements,
        tailPlan.leadingSpokeLeg ? 1 : 0
      );

      dispatch({
        type: removeElements.type,
        payload: oldIds,
        meta: { skipHistory: true },
      });
      dispatch({
        type: addElements.type,
        payload: newElements,
        meta: { skipHistory: true },
      });
      dispatch(
        addHistoryAction({
          id: generateId(),
          type: 'replace',
          timestamp: Date.now(),
          elementIds: newElements.map((el) => el.id),
          previousState: oldElements,
          newState: newElements,
        })
      );
      return true;
    },
    [
      dispatch,
      effectiveDxfUnitsPerMeter,
      effectiveScaleFactor,
      elements,
      getNextEntityId,
      getNextEntityName,
      planRunRebuild,
      segToPointElement,
    ]
  );

  // Intelligent loop closure: when the operator clicks near an earlier corner of
  // the polyline, route the head back to that corner with legal horizontal/
  // vertical approach legs, then rebuild the run as a closed constrained loop.
  // The closure corner (and every other corner) is solved by the shared
  // generator — fixed-radius curves with tangent straights on both sides, never a
  // raw endpoint weld, illegal tangent, arbitrary angle, back-to-back curve, or
  // tiny filler. Returns true when a legal loop was produced and drawing should
  // finish; false leaves everything untouched so the click is treated normally.
  const closePointDrawLoop = useCallback(
    (anchor: CloseAnchor): boolean => {
      const verts = pointDraw.vertices;
      if (anchor.index < 0 || anchor.index >= verts.length) return false;

      const candidates = closureApproachCandidates(
        verts[anchor.index],
        verts[verts.length - 1]
      );
      if (candidates.length === 0) return false;

      const tail = verts.slice(0, anchor.index + 1);
      const loop = verts.slice(anchor.index);
      for (const wps of candidates) {
        const closed = [...loop, ...wps];
        const committed =
          anchor.index === 0
            ? regenerateRun(closed, true)
            : commitLoopWithLeadIn(tail, closed);
        if (!committed) continue;
        lastPointStraightRef.current = null;
        firstPointStraightRef.current = null;
        pointDrawSessionIdsRef.current.clear();
        dispatch(clearPointDraw());
        return true;
      }
      return false;
    },
    [pointDraw.vertices, regenerateRun, commitLoopWithLeadIn, dispatch]
  );

  // Maximum angle (deg) a *rail endpoint's* outward direction may deviate from a
  // cardinal axis and still be connectable with a Manhattan tangent approach.
  // Junction ports are always connectable — they use spoke-aligned routing
  // (including diagonals on 8-way hubs).
  const PORT_TANGENT_TOL = 20;

  // The two endpoints of a straight/curve rail plus each end's *outward* tangent
  // (the direction a track would continue if extended past that end). Used so
  // Point Draw can start from, or connect tangentially to, an existing rail's
  // free end.
  const getRailEndpointTangents = useCallback((el: LayoutElement) => {
    const rt = resolveRailType(el);
    const pts = getRailEndpoints(el);
    const rot = ((el.rotation || 0) % 360 + 360) % 360;
    const norm = (d: number) => ((d % 360) + 360) % 360;
    if (rt === 'straight' && pts.length === 2) {
      return [
        { pt: pts[0], outwardDeg: norm(rot + 180) },
        { pt: pts[1], outwardDeg: norm(rot) },
      ];
    }
    if (isCurveRailType(rt) && pts.length === 2) {
      const theta = getCurveSweepAngle(rt);
      return [
        { pt: pts[0], outwardDeg: norm(270 + rot) },
        { pt: pts[1], outwardDeg: norm(theta + 90 + rot) },
      ];
    }
    return [];
  }, []);

  // Scan for connection targets near a world point: circular-junction ports plus
  // *logical* open ends of straight/curve runs. Internal joints between abutting
  // primitives (A—P1—P2—B splits) are never offered. Excludes the run currently
  // being drawn. Discovery rings use a fixed *screen-pixel* radius so they feel
  // identical at every zoom; snap distance is unchanged.
  const findPortSnap = useCallback(
    (
      worldPt: { x: number; y: number }
    ): {
      ports: { x: number; y: number; legal: boolean }[];
      target: { tip: { x: number; y: number }; arrivalHeading: number } | null;
      /**
       * Connection targets in preference order, best first. `target` is simply
       * the head of this list; the tail lets the caller retry when the preferred
       * spoke turns out to be unreachable from where the run currently is.
       */
      targets: { tip: { x: number; y: number }; arrivalHeading: number }[];
      /**
       * Plain closest snappable tip, ignoring the cardinal-first ranking.
       *
       * Ranking answers "where should a run arrive", which is not the same
       * question as "which tip did the operator point at". Starting a run, and
       * measuring how near a port is, both want the tip under the cursor — a
       * corner spoke is a perfectly good place to leave an 8-way hub from, even
       * though it is a poor place to arrive at.
       */
      nearest: { tip: { x: number; y: number }; arrivalHeading: number } | null;
      /** Closest axis-legal tip within the discovery radius (visual highlight). */
      closestTip: { x: number; y: number } | null;
    } => {
      const zoom = viewport.zoom || 1;
      // Snap radius unchanged — do not couple to discovery advertising.
      const snapDist = Math.max(gridSettings.snapGridSize, 18 / zoom);
      const nearestAxis = (deg: number) => (((Math.round(deg / 90) * 90) % 360) + 360) % 360;
      const angDiff = (a: number, b: number) => {
        let d = Math.abs((((a - b) % 360) + 360) % 360);
        if (d > 180) d = 360 - d;
        return d;
      };
      const near = (a: { x: number; y: number }, b: { x: number; y: number }, tol: number) =>
        Math.hypot(a.x - b.x, a.y - b.y) <= tol;
      // Proximity in screen pixels: worldDelta * zoom == screen distance.
      const screenDistPx = (pt: { x: number; y: number }) =>
        Math.hypot(pt.x - worldPt.x, pt.y - worldPt.y) * zoom;

      const ports: { x: number; y: number; legal: boolean }[] = [];
      // Every discovered junction spoke, not just those inside the snap radius.
      // Once any one spoke is close enough to engage a hub, the rest of that
      // hub's spokes become candidates too, and the flat ones routinely sit
      // further from the cursor than the diagonal the operator drifted onto.
      const spokes: {
        railId: string;
        tip: { x: number; y: number };
        arrivalHeading: number;
        distance: number;
        cardinal: boolean;
      }[] = [];
      let endTarget: { tip: { x: number; y: number }; arrivalHeading: number } | null = null;
      let endDist = Infinity;
      let closestTip: { x: number; y: number } | null = null;
      let closestScreenPx = Infinity;

      const consider = (
        pt: { x: number; y: number },
        outwardDeg: number,
        kind: 'port' | 'endpoint',
        railId: string
      ) => {
        const dPx = screenDistPx(pt);
        if (dPx > POINT_DRAW_DISCOVER_RADIUS_PX) return;
        // Junction ports are always legal (spoke-aligned routing, incl. diagonals).
        // Free rail ends still require a near-cardinal tangent for Manhattan approach.
        const axis = nearestAxis(outwardDeg);
        const legal =
          kind === 'port' || angDiff(outwardDeg, axis) <= PORT_TANGENT_TOL;
        ports.push({ x: pt.x, y: pt.y, legal });
        if (legal && dPx < closestScreenPx) {
          closestScreenPx = dPx;
          closestTip = pt;
        }
        // Snapping still uses world-space snapDist (behavior unchanged).
        const dWorld = dPx / zoom;
        if (!legal) return;
        // Approach into the tip along the spoke (or cardinal axis for rail ends).
        const approachAxis = kind === 'port' ? normDeg(outwardDeg) : axis;
        const hit = { tip: pt, arrivalHeading: normDeg(approachAxis + 180) };
        if (kind === 'port') {
          spokes.push({
            railId,
            tip: pt,
            arrivalHeading: hit.arrivalHeading,
            distance: dWorld,
            cardinal: isCardinalDeg(outwardDeg),
          });
          return;
        }
        if (dWorld > snapDist) return;
        if (dWorld < endDist) {
          endDist = dWorld;
          endTarget = hit;
        }
      };

      const sessionIds = pointDrawSessionIdsRef.current;
      const rails = Object.values(elements).filter(
        (el) => el.metadata.visible && ALL_RAIL_TYPES.has(el.type)
      );
      const logical = getLogicalPointDrawEndpoints(rails, { excludeIds: sessionIds });
      const tipTol = ENDPOINT_TOLERANCE;

      for (const el of rails) {
        if (sessionIds.has(el.id)) continue;
        const rt = resolveRailType(el);
        if (rt === 'junction-circle') {
          for (const p of getRotaryPorts(el)) {
            const isLogicalPort = logical.some(
              (l) =>
                l.role === 'junction-port' &&
                l.railId === el.id &&
                near(l.tip, p.tip, tipTol)
            );
            if (isLogicalPort) consider(p.tip, p.outwardDeg, 'port', el.id);
          }
          continue;
        }
        for (const e of getRailEndpointTangents(el)) {
          const isLogicalEnd = logical.some(
            (l) =>
              l.role === 'rail-end' &&
              l.railId === el.id &&
              near(l.tip, e.pt, tipTol)
          );
          if (isLogicalEnd) consider(e.pt, e.outwardDeg, 'endpoint', el.id);
        }
      }

      // Engage the hub whose nearest spoke is inside the snap radius, then let
      // `rankSpokePorts` decide which of that hub's spokes to connect on: the
      // one pointed squarely at, or — when the cursor is merely near the hub —
      // a flat port, since only a cardinal arrival can be filleted into a curve.
      const engaged = spokes.filter((s) => s.distance <= snapDist);
      let portTargets: { tip: { x: number; y: number }; arrivalHeading: number }[] = [];
      let nearestPort: { tip: { x: number; y: number }; arrivalHeading: number } | null =
        null;
      if (engaged.length > 0) {
        const closest = engaged.reduce((a, b) => (b.distance < a.distance ? b : a));
        nearestPort = { tip: closest.tip, arrivalHeading: closest.arrivalHeading };
        portTargets = rankSpokePorts(
          spokes.filter((s) => s.railId === closest.railId)
        ).map((s) => ({ tip: s.tip, arrivalHeading: s.arrivalHeading }));
      }

      const targets = portTargets.length > 0 ? portTargets : endTarget ? [endTarget] : [];
      return {
        ports,
        target: targets[0] ?? null,
        targets,
        nearest: nearestPort ?? endTarget,
        closestTip,
      };
    },
    [elements, gridSettings.snapGridSize, viewport.zoom, getRotaryPorts, getRailEndpointTangents]
  );

  // True when the pointer is on an existing rail away from every logical
  // connection point (occupied middle / internal segment boundary).
  const isPointDrawBlockedByTrack = useCallback(
    (worldPt: { x: number; y: number }) => {
      const sessionIds = pointDrawSessionIdsRef.current;
      const perpTol = Math.max(gridSettings.snapGridSize * 0.6, 10 / viewport.zoom);
      return isOccupiedTrackInterior(worldPt, Object.values(elements), {
        excludeIds: sessionIds,
        perpTolerance: perpTol,
      });
    },
    [elements, gridSettings.snapGridSize, viewport.zoom]
  );

  // Plan a legal route from the head to a junction port so the final leg
  // arrives collinear with the port's spoke (a tangent connection), approaching
  // from the outward side so it never crosses the junction body.
  //
  // When the head sits on (or already follows) another junction spoke — especially
  // diagonal ports — prefer outbound stubs + a Manhattan bridge so facing `/` and
  // `\` ports get a short spoke-out + horizontal link instead of a deep V onto the
  // target ray. Cardinal↔cardinal still uses the Manhattan L approach.
  const routeHeadToPort = useCallback(
    (H: { x: number; y: number }, arrivalHeading: number, P: { x: number; y: number }) => {
      const stubLen = Math.max(segmentSizing.radiusUnits, gridSettings.snapGridSize);
      const target = { tip: P, outwardDeg: normDeg(arrivalHeading + 180) };

      const spokeAtHead = findJunctionSpokeAt(H);
      if (spokeAtHead != null) {
        const bridged = routeBetweenSpokePorts(
          { tip: H, outwardDeg: spokeAtHead },
          target,
          stubLen
        );
        if (bridged && bridged.length > 0) return bridged;
      }

      const prev = lastPointStraightRef.current;
      if (prev && !isCardinalDeg(prev.heading)) {
        const bridged = routeBetweenSpokePorts(
          { tip: H, outwardDeg: prev.heading, headIsStub: true },
          target,
          stubLen
        );
        if (bridged && bridged.length > 0) return bridged;
      }

      // Diagonal port: stay on a cardinal axis and turn onto the spoke only for
      // the final short leg. Without this the head is projected onto the spoke
      // ray far from the hub, which draws a deep V. A run with no committed
      // heading yet (lone first vertex) lets the solver pick the best axis.
      const shortSpokeApproach = routeCardinalHeadToDiagonalPort(
        H,
        prev && isCardinalDeg(prev.heading) ? prev.heading : null,
        target
      );
      if (shortSpokeApproach) return shortSpokeApproach;

      return routeHeadToSpokePort(H, arrivalHeading, P);
    },
    [findJunctionSpokeAt, segmentSizing.radiusUnits, gridSettings.snapGridSize]
  );

  // Commit a tangent connection from the head to a junction port / free rail
  // endpoint. Cardinal (Manhattan) approaches rebuild the whole run through the
  // shared orthogonal generator. Spoke / diagonal approaches append ordinary
  // straights (any angle) from the current head so 8-way hubs can grow legs.
  const connectHeadToPort = useCallback(
    (
      H: { x: number; y: number },
      targets: { tip: { x: number; y: number }; arrivalHeading: number }[]
    ): boolean => {
      const verts = pointDraw.vertices;
      if (verts.length === 0) return false;
      // Walk the ranked spokes: the preferred (cardinal) one may sit on the far
      // side of the hub from the head, which no legal approach can reach without
      // crossing the junction body. Falling through preserves the connection
      // instead of refusing the click.
      const routed = targets.reduce<
        { target: (typeof targets)[number]; wps: { x: number; y: number }[] } | null
      >((found, target) => {
        if (found) return found;
        const candidate = routeHeadToPort(H, target.arrivalHeading, target.tip);
        return candidate && candidate.length > 0 ? { target, wps: candidate } : null;
      }, null);
      if (!routed) return false;
      const wps = routed.wps;

      const full = [...verts, ...wps];
      if (isManhattanPolyline(full) && regenerateRun(full, false)) {
        lastPointStraightRef.current = null;
        firstPointStraightRef.current = null;
        pointDrawSessionIdsRef.current.clear();
        dispatch(clearPointDraw());
        return true;
      }

      // Non-Manhattan (spoke / diagonal): append straights from the live head.
      const points = [H, ...wps];
      const newElements: LayoutElement[] = [];
      for (let i = 0; i < points.length - 1; i++) {
        const a = points[i];
        const b = points[i + 1];
        if (Math.hypot(b.x - a.x, b.y - a.y) < 1e-6) continue;
        const { element } = buildPointStraight(a, b);
        newElements.push(element);
      }
      if (newElements.length === 0) return false;

      dispatch(addElements(newElements));
      lastPointStraightRef.current = null;
      firstPointStraightRef.current = null;
      pointDrawSessionIdsRef.current.clear();
      dispatch(clearPointDraw());
      return true;
    },
    [pointDraw.vertices, routeHeadToPort, regenerateRun, buildPointStraight, dispatch]
  );

  /**
   * Turn corners are advertised like ports: a fillet arc is only a fraction of a
   * grid long, so the corner — not the arc centerline — is the target the
   * operator aims at. `discover` mirrors the port discovery ring; `snap` is the
   * radius a click converts within.
   */
  const findTurnSnap = useCallback(
    (worldPt: { x: number; y: number }) => {
      const zoom = viewport.zoom || 1;
      const discover = POINT_DRAW_DISCOVER_RADIUS_PX / zoom;
      const snap = Math.max(gridSettings.snapGridSize, 24 / zoom);
      const hit = findCurveTurnNear(
        worldPt,
        Object.values(elements),
        discover,
        pointDrawSessionIdsRef.current
      );
      if (!hit) return null;
      return { hit, withinSnap: hit.distance <= snap };
    },
    [elements, gridSettings.snapGridSize, viewport.zoom]
  );

  /**
   * Clicking onto an existing curve turn converts it into a Circular Junction
   * and attaches the current Point Draw head to a free branch port.
   */
  const commitPointDrawCurveJunction = useCallback(
    (
      cur: { x: number; y: number },
      worldPointer: { x: number; y: number }
    ): 'committed' | 'rejected' | 'not-applicable' => {
      const turn = findTurnSnap(worldPointer);
      if (!turn?.withinSnap) return 'not-applicable';

      const plan = planPointDrawCurveJunctionInsert(
        turn.hit.host,
        cur,
        Object.values(elements),
        {
          nextName: (type) => getNextEntityName(type),
          nextId: (type, railType) => getNextEntityId(type, railType),
          lengthMetersForWidth: (width) =>
            worldUnitsToEffectiveMeters(
              width,
              effectiveScaleFactor,
              effectiveDxfUnitsPerMeter
            ),
        }
      );
      if (!plan.ok) return 'rejected';

      const junction = plan.addElements.find((el) => el.id === plan.selectId);
      if (!junction) return 'rejected';

      const jw = junction.dimensions.width;
      const jh = junction.dimensions.height;
      const jrot = ((junction.rotation || 0) * Math.PI) / 180;
      const jcos = Math.cos(jrot);
      const jsin = Math.sin(jrot);
      const center = {
        x: junction.position.x + (jw / 2) * jcos - (jh / 2) * jsin,
        y: junction.position.y + (jw / 2) * jsin + (jh / 2) * jcos,
      };
      const outwardDeg =
        (Math.atan2(plan.entryPort.y - center.y, plan.entryPort.x - center.x) * 180) /
        Math.PI;
      const nearestAxis = (deg: number) =>
        (((Math.round(deg / 90) * 90) % 360) + 360) % 360;
      const arrivalHeading = (nearestAxis(outwardDeg) + 180) % 360;

      const route = routeHeadToPort(cur, arrivalHeading, plan.entryPort);
      const approachPoints: { x: number; y: number }[] = [];
      if (route && route.length > 0) {
        approachPoints.push(cur, ...route);
      } else if (
        Math.abs(cur.x - plan.entryPort.x) < 1e-6 ||
        Math.abs(cur.y - plan.entryPort.y) < 1e-6
      ) {
        approachPoints.push(cur, plan.entryPort);
      } else {
        return 'rejected';
      }

      const approachElements: LayoutElement[] = [];
      for (let i = 0; i < approachPoints.length - 1; i++) {
        const a = approachPoints[i];
        const b = approachPoints[i + 1];
        if (Math.hypot(b.x - a.x, b.y - a.y) < 1e-6) continue;
        approachElements.push(buildPointStraight(a, b).element);
      }
      if (approachElements.length === 0) return 'rejected';

      const hostAndAdjacent = plan.removeIds
        .map((id) => elements[id])
        .filter((el): el is LayoutElement => !!el);

      dispatch({
        type: removeElements.type,
        payload: plan.removeIds,
        meta: { skipHistory: true },
      });
      const newElements = [...plan.addElements, ...approachElements];
      dispatch({
        type: addElements.type,
        payload: newElements,
        meta: { skipHistory: true },
      });
      dispatch(
        addHistoryAction({
          id: generateId(),
          type: 'replace',
          timestamp: Date.now(),
          elementIds: newElements.map((element) => element.id),
          previousState: hostAndAdjacent,
          newState: newElements,
        })
      );

      resetPointDrawRun();
      return 'committed';
    },
    [
      buildPointStraight,
      dispatch,
      effectiveDxfUnitsPerMeter,
      effectiveScaleFactor,
      elements,
      findTurnSnap,
      getNextEntityName,
      getNextEntityId,
      resetPointDrawRun,
      routeHeadToPort,
    ]
  );

  // Keyboard controls active only while Point Draw is on:
  //   Esc   → cancel the pending polyline, or leave the tool if nothing is pending
  //   Enter → finish the pending polyline
  useEffect(() => {
    if (!pointDraw.active) return;

    const hasPendingRun = pointDraw.vertices.length > 0;

    const handleKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        lastPointStraightRef.current = null;
        firstPointStraightRef.current = null;
        pointDrawSessionIdsRef.current.clear();
        dispatch(clearPointDraw());
        // First Esc abandons the run in progress so the next click starts a new
        // line; a second Esc (nothing pending) exits the tool altogether.
        if (hasPendingRun) resetPointDrawRun();
        else dispatch(activateCanvasTool(null));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        resetPointDrawRun();
        return;
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [pointDraw.active, pointDraw.vertices.length, dispatch, resetPointDrawRun]);

  // Drop the Point Draw rubber-band preview when the tool turns off.
  useEffect(() => {
    if (!pointDraw.active) {
      setPointPreview(null);
      setPortHints({ ports: [], activeTip: null, route: null });
      setPointDrawCenterline(null);
      lastPointStraightRef.current = null;
      firstPointStraightRef.current = null;
      pointDrawSessionIdsRef.current.clear();
    }
  }, [pointDraw.active]);

  useEffect(() => {
    if (!trackNodePlacementActive) {
      setTrackNodePreview(null);
      return;
    }

    const handleKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable ||
        e.ctrlKey ||
        e.metaKey ||
        e.altKey
      ) {
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        dispatch(setTrackNodePlacementActive(false));
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [trackNodePlacementActive, dispatch]);

  // Esc cancels an in-progress route start/end capture (and rejects the waiting generation flow).
  useEffect(() => {
    if (!routeEndpointPlacement) return;
    const handleKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        dispatch(resetRouteEndpoints());
        cancelEndpointCapture();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [routeEndpointPlacement, dispatch]);

  // Drop the equipment draw cursor preview when the tool is off.
  useEffect(() => {
    if (!eqDraw.active) setEqCursor(null);
  }, [eqDraw.active]);

  // Track the toolbox item during HTML5 drag (getData is empty on dragover in Chrome).
  useEffect(() => {
    const onStart = (e: Event) => {
      const detail = (e as CustomEvent<ToolboxItem>).detail;
      draggingToolboxItemRef.current = detail ?? null;
    };
    const onEnd = () => {
      draggingToolboxItemRef.current = null;
      setDragRailMeasure(null);
      setDropPreview(null);
    };
    window.addEventListener('toolbox:drag-item-start', onStart);
    window.addEventListener('toolbox:drag-item-end', onEnd);
    return () => {
      window.removeEventListener('toolbox:drag-item-start', onStart);
      window.removeEventListener('toolbox:drag-item-end', onEnd);
    };
  }, []);

  // Unique element ids involved in clearance conflicts
  const conflictIds = React.useMemo(() => {
    const ids = new Set<string>();
    validation.conflicts.forEach((c) => {
      ids.add(c.aId);
      ids.add(c.bId);
    });
    return ids;
  }, [validation.conflicts]);

  // Locked layers are rendered inside a shaded drag-wrapper so users can move
  // the whole locked set without unlocking individual items.
  const lockedLayerWrappers = useMemo(() => {
    const wrappers: Array<{
      layerId: string;
      x: number;
      y: number;
      width: number;
      height: number;
      elementIds: string[];
    }> = [];

    for (const layerId of layoutLayerOrder) {
      const layer = layoutLayers[layerId];
      if (!layer || !layer.visible || !layer.locked || layer.elementIds.length === 0) continue;

      const layerElements = layer.elementIds
        .map((id) => elements[id])
        .filter((el): el is LayoutElement => !!el && el.metadata.visible);
      if (layerElements.length === 0) continue;

      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;

      for (const el of layerElements) {
        const box = getAABB(el);
        if (box.minX < minX) minX = box.minX;
        if (box.minY < minY) minY = box.minY;
        if (box.maxX > maxX) maxX = box.maxX;
        if (box.maxY > maxY) maxY = box.maxY;
      }

      const pad = 8;
      wrappers.push({
        layerId,
        x: minX - pad,
        y: minY - pad,
        width: Math.max(1, maxX - minX + pad * 2),
        height: Math.max(1, maxY - minY + pad * 2),
        elementIds: layerElements.map((el) => el.id),
      });
    }

    return wrappers;
  }, [layoutLayerOrder, layoutLayers, elements]);

  const handleLockedLayerTransformStart = useCallback(
    (layerId: string, wrapperX: number, wrapperY: number, wrapperWidth: number, wrapperHeight: number) => {
      const layer = layoutLayers[layerId];
      if (!layer) return;

      setActiveTransformWrapper({
        id: layerId,
        startX: wrapperX,
        startY: wrapperY,
        startWidth: wrapperWidth,
        startHeight: wrapperHeight,
      });

      layerDragStartPositionsRef.current = {};
      layerDragSnapshotRef.current = {};

      for (const elementId of layer.elementIds) {
        const element = elements[elementId];
        if (!element) continue;

        layerDragStartPositionsRef.current[elementId] = {
          x: element.position.x,
          y: element.position.y,
        };
        layerDragSnapshotRef.current[elementId] = {
          ...element,
          position: { ...element.position },
          dimensions: { ...element.dimensions },
          metadata: { ...element.metadata },
          customProperties: element.customProperties
            ? { ...element.customProperties }
            : undefined,
        };
      }
    },
    [layoutLayers, elements]
  );

  const handleLockedLayerTransform = useCallback(
    (layerId: string, node: Konva.Group, wrapperOrigin: { x: number; y: number }) => {
      const layer = layoutLayers[layerId];
      if (!layer) return;

      const scaleX = node.scaleX();
      const scaleY = node.scaleY();
      const rotation = node.rotation();
      const transform = node.getTransform();

      const transforms = layer.elementIds.flatMap((id) => {
        const startElement = layerDragSnapshotRef.current[id];
        if (!startElement) return [];

        const localX = startElement.position.x - wrapperOrigin.x;
        const localY = startElement.position.y - wrapperOrigin.y;
        
        const newPos = transform.point({ x: localX, y: localY });

        const minDimension =
          ALL_RAIL_TYPES.has(startElement.type) || startElement.type === 'oht-vehicle' ? 0.001 : 5;
        const allowHeightResize =
          startElement.type === 'stocker' ||
          ALL_RAIL_TYPES.has(startElement.type) ||
          startElement.type === 'oht-vehicle';

        return [
          {
            id,
            transform: {
              position: { x: newPos.x, y: newPos.y },
              rotation: ((startElement.rotation + rotation) % 360 + 360) % 360,
              dimensions: {
                width: Math.max(minDimension, startElement.dimensions.width * scaleX),
                height: allowHeightResize
                  ? Math.max(minDimension, startElement.dimensions.height * scaleY)
                  : startElement.dimensions.height,
              },
            },
          },
        ];
      });

      if (transforms.length > 0) {
        dispatch(transformElements(transforms));
      }
    },
    [dispatch, layoutLayers]
  );

  const handleLockedLayerTransformEnd = useCallback(
    (layerId: string, node: Konva.Group, wrapperOrigin: { x: number; y: number }) => {
      const layer = layoutLayers[layerId];
      if (!layer) return;

      const scaleX = node.scaleX();
      const scaleY = node.scaleY();
      const rotation = node.rotation();
      const transform = node.getTransform();

      const movedElementIds = layer.elementIds.filter(
        (id) => !!layerDragSnapshotRef.current[id]
      );

      const previousState = movedElementIds
        .map((id) => layerDragSnapshotRef.current[id])
        .filter((el): el is LayoutElement => !!el);
        
      const newState = movedElementIds
        .map((id) => {
          const startElement = layerDragSnapshotRef.current[id];
          if (!startElement) return null;

          const localX = startElement.position.x - wrapperOrigin.x;
          const localY = startElement.position.y - wrapperOrigin.y;
          const newPos = transform.point({ x: localX, y: localY });

          const minDimension =
            ALL_RAIL_TYPES.has(startElement.type) || startElement.type === 'oht-vehicle' ? 0.001 : 5;
          const allowHeightResize =
            startElement.type === 'stocker' ||
            ALL_RAIL_TYPES.has(startElement.type) ||
            startElement.type === 'oht-vehicle';

          return {
            ...startElement,
            position: { x: newPos.x, y: newPos.y },
            rotation: ((startElement.rotation + rotation) % 360 + 360) % 360,
            dimensions: {
              width: Math.max(minDimension, startElement.dimensions.width * scaleX),
              height: allowHeightResize
                ? Math.max(minDimension, startElement.dimensions.height * scaleY)
                : startElement.dimensions.height,
            },
          };
        })
        .filter((el): el is LayoutElement => !!el);

      if (previousState.length > 0 && newState.length > 0) {
        dispatch(
          addHistoryAction({
            id: generateId(),
            type: 'resize',
            timestamp: Date.now(),
            elementIds: movedElementIds,
            previousState,
            newState,
          })
        );
      }

      node.scaleX(1);
      node.scaleY(1);
      node.rotation(0);

      layerDragStartPositionsRef.current = {};
      layerDragSnapshotRef.current = {};
      setActiveTransformWrapper(null);
    },
    [dispatch, layoutLayers]
  );

  const handleLockedLayerDragStart = useCallback(
    (layerId: string, wrapperX: number, wrapperY: number, wrapperWidth: number, wrapperHeight: number) => {
      const layer = layoutLayers[layerId];
      if (!layer) return;

      setActiveTransformWrapper({
        id: layerId,
        startX: wrapperX,
        startY: wrapperY,
        startWidth: wrapperWidth,
        startHeight: wrapperHeight,
      });

      layerDragStartPositionsRef.current = {};
      layerDragSnapshotRef.current = {};

      for (const elementId of layer.elementIds) {
        const element = elements[elementId];
        if (!element) continue;

        layerDragStartPositionsRef.current[elementId] = {
          x: element.position.x,
          y: element.position.y,
        };
        layerDragSnapshotRef.current[elementId] = {
          ...element,
          position: { ...element.position },
          dimensions: { ...element.dimensions },
          metadata: { ...element.metadata },
          customProperties: element.customProperties ? { ...element.customProperties } : undefined,
        };
      }
    },
    [layoutLayers, elements]
  );

  const handleLockedLayerDragMove = useCallback(
    (
      layerId: string,
      wrapperOrigin: { x: number; y: number },
      current: { x: number; y: number }
    ) => {
      const layer = layoutLayers[layerId];
      if (!layer) return;

      const dx = current.x - wrapperOrigin.x;
      const dy = current.y - wrapperOrigin.y;
      const transforms = layer.elementIds.flatMap((id) => {
        const start = layerDragStartPositionsRef.current[id];
        if (!start) return [];
        return [{ id, transform: { position: { x: start.x + dx, y: start.y + dy } } }];
      });

      if (transforms.length > 0) {
        dispatch(transformElements(transforms));
      }
    },
    [dispatch, layoutLayers]
  );

  const handleLockedLayerDragEnd = useCallback(
    (
      layerId: string,
      wrapperOrigin: { x: number; y: number },
      current: { x: number; y: number }
    ) => {
      const layer = layoutLayers[layerId];
      if (!layer) {
        setActiveTransformWrapper(null);
        return;
      }

      const dx = current.x - wrapperOrigin.x;
      const dy = current.y - wrapperOrigin.y;
      const movedElementIds = layer.elementIds.filter(
        (id) => !!layerDragStartPositionsRef.current[id]
      );

      const previousState = movedElementIds
        .map((id) => layerDragSnapshotRef.current[id])
        .filter((el): el is LayoutElement => !!el);
      const newState = movedElementIds
        .map((id) => {
          const before = layerDragSnapshotRef.current[id];
          const start = layerDragStartPositionsRef.current[id];
          if (!before || !start) return null;
          return {
            ...before,
            position: { x: start.x + dx, y: start.y + dy },
          };
        })
        .filter((el): el is LayoutElement => !!el);

      if ((dx !== 0 || dy !== 0) && previousState.length > 0 && newState.length > 0) {
        dispatch(
          addHistoryAction({
            id: generateId(),
            type: 'move',
            timestamp: Date.now(),
            elementIds: movedElementIds,
            previousState,
            newState,
          })
        );
      }

      layerDragStartPositionsRef.current = {};
      layerDragSnapshotRef.current = {};
      setActiveTransformWrapper(null);
    },
    [dispatch, layoutLayers, setActiveTransformWrapper]
  );

  // Update stage size on mount, resize, and when returning from the 3D view.
  // CSS `hidden` (display:none) collapses the container to 0×0 — window resize
  // alone does not fire when the class is removed, so ResizeObserver + an
  // explicit remeasure on view3dOpen are both required.
  useEffect(() => {
    const updateSize = () => {
      if (!containerRef.current) return;
      const width = containerRef.current.offsetWidth;
      const height = containerRef.current.offsetHeight;
      // Ignore the collapsed 0×0 size while the 2D canvas is CSS-hidden under 3D.
      if (width <= 0 || height <= 0) return;
      setStageSize((prev) =>
        prev.width === width && prev.height === height ? prev : { width, height }
      );
      setViewport({ canvasWidth: width, canvasHeight: height });
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    const observer =
      typeof ResizeObserver !== 'undefined' && containerRef.current
        ? new ResizeObserver(updateSize)
        : null;
    if (observer && containerRef.current) observer.observe(containerRef.current);
    return () => {
      window.removeEventListener('resize', updateSize);
      observer?.disconnect();
    };
  }, [setViewport, view3dOpen, view3dSceneRevealed]);

  // Sync Redux zoomLevel → viewportStore. Preserve the world point under the
  // visible (chrome-inset) center so zoom-only updates don't shove the layout.
  useEffect(() => {
    const vp = viewportStore.getState();
    if (zoomLevel === vp.zoom) return;
    const { canvasWidth, canvasHeight, panX, panY, zoom } = vp;
    if (canvasWidth > 0 && canvasHeight > 0 && zoom > 0) {
      const insets = canvasChromeInsets({ leftSidebarOpen, rightSidebarOpen });
      const insetL = insets.left ?? 0;
      const insetR = insets.right ?? 0;
      const insetT = insets.top ?? 0;
      const insetB = insets.bottom ?? 0;
      const viewW = Math.max(canvasWidth - insetL - insetR, 1);
      const viewH = Math.max(canvasHeight - insetT - insetB, 1);
      const viewCX = insetL + viewW / 2;
      const viewCY = insetT + viewH / 2;
      const cx = (viewCX - panX) / zoom;
      const cy = (viewCY - panY) / zoom;
      setViewport({
        zoom: zoomLevel,
        panX: viewCX - cx * zoomLevel,
        panY: viewCY - cy * zoomLevel,
      });
    } else {
      setViewport({ zoom: zoomLevel });
    }
  }, [zoomLevel, leftSidebarOpen, rightSidebarOpen, setViewport]);

  // Auto-fit real DXF imports. Empty blank workspaces stay at DEFAULT_ZOOM (700%).
  // Loaded layouts (with elements) keep their restored/fit zoom — never force 700%.
  // Content identity, not bounds object identity. Redux/import processing may
  // recreate an equivalent bounds object during unrelated actions; using the
  // object reference caused an unwanted second fit/zoom-out.
  const floorFitKey = floorplanBounds
    ? [
        floorplanBounds.minX,
        floorplanBounds.minY,
        floorplanBounds.maxX,
        floorplanBounds.maxY,
        fpPolylines.length,
      ].join('|')
    : `blank|${fpPolylines.length}`;
  const lastFitKey = useRef<string | null>(null);
  const hasLayoutElements = elementOrder.length > 0;
  useEffect(() => {
    if (stageSize.width === 0 || stageSize.height === 0) return;

    // Truly blank (no DXF, no layout): never fit placeholder 0–2000 bounds (~29%).
    if (fpPolylines.length === 0 && !hasLayoutElements) {
      const blankBoundsChanged = lastFitKey.current !== floorFitKey;
      const zoomTooSmallForBlank = zoomLevel < 1;
      if (blankBoundsChanged || zoomTooSmallForBlank) {
        setViewport({
          zoom: DEFAULT_ZOOM,
          panX: stageSize.width / 2,
          panY: stageSize.height / 2,
        });
        setZoomIfChanged(DEFAULT_ZOOM);
        lastFitKey.current = floorFitKey;
      }
      return;
    }

    // Layout JSON loaded without DXF — leave zoom alone (Toolbar restores/fits it).
    if (fpPolylines.length === 0) {
      lastFitKey.current = floorFitKey;
      return;
    }

    const b = getRobustFitBounds(floorplanBounds, fpBBoxes);
    if (!b) return;
    if (lastFitKey.current === floorFitKey) return; // only fit on new import

    const bw = b.maxX - b.minX;
    const bh = b.maxY - b.minY;
    if (bw <= 0 || bh <= 0) {
      lastFitKey.current = floorFitKey;
      return;
    }

    const insets = canvasChromeInsets({ leftSidebarOpen, rightSidebarOpen });
    const fit = computeFitViewport(b, stageSize.width, stageSize.height, 0.9, insets);
    setViewport(fit);
    setZoomIfChanged(fit.zoom);
    lastFitKey.current = floorFitKey;
  }, [
    floorplanBounds,
    floorFitKey,
    fpBBoxes,
    fpPolylines.length,
    hasLayoutElements,
    stageSize,
    zoomLevel,
    leftSidebarOpen,
    rightSidebarOpen,
    setViewport,
    setZoomIfChanged,
  ]);

  // Handle drop from toolbox
  const dropToolboxItem = useCallback(
    (
      item: ToolboxItem,
      worldPt: { x: number; y: number },
      propertyOverrides?: Record<string, unknown>
    ) => {
      const baseName = getNextEntityName(item.type as ElementType);
      const isConnectorDrop =
        item.type === 'overhead-rail' &&
        String(
          (propertyOverrides?.railType ?? item.defaultProperties?.railType) || ''
        ) === 'connector';

      const placement = computeToolboxDropPlacement(
        item,
        worldPt,
        {
          elements: Object.values(elements),
          gridSettings,
          scaleFactor: effectiveScaleFactor,
          dxfUnitsPerMeter: effectiveDxfUnitsPerMeter,
          stops,
          name: isConnectorDrop ? baseName.replace(/^Rail/, 'Connector') : baseName,
        },
        propertyOverrides
      );

      const { element: placed, railType: dropRailType } = placement;
      const newElement: LayoutElement = {
        ...placed,
        id: getNextEntityId(item.type as ElementType, dropRailType || undefined),
        position: {
          ...placed.position,
          z: defaultElementZ(
            item.type as ElementType,
            placed.customProperties as Record<string, unknown>
          ),
        },
        dimensions: {
          ...placed.dimensions,
          depth:
            typeof placed.dimensions.depth === 'number' && placed.dimensions.depth > 0
              ? placed.dimensions.depth
              : defaultElementDepth(item.type as ElementType),
        },
      };

      dispatch(addElement(newElement));
      dispatch(selectElement(newElement.id));
    },
    [
      dispatch,
      elements,
      effectiveScaleFactor,
      effectiveDxfUnitsPerMeter,
      getNextEntityName,
      getNextEntityId,
      gridSettings,
      stops,
    ]
  );

  const commitRailInsert = useCallback(
    (plan: {
      removeIds: string[];
      addElements: LayoutElement[];
      selectId: string;
    }) => {
      const oldElements = plan.removeIds
        .map((id) => elements[id])
        .filter((el): el is LayoutElement => !!el);

      dispatch({
        type: removeElements.type,
        payload: plan.removeIds,
        meta: { skipHistory: true },
      });
      dispatch({
        type: addElements.type,
        payload: plan.addElements,
        meta: { skipHistory: true },
      });
      dispatch(
        addHistoryAction({
          id: generateId(),
          type: 'replace',
          timestamp: Date.now(),
          elementIds: plan.addElements.map((el) => el.id),
          previousState: oldElements,
          newState: plan.addElements,
        })
      );
      dispatch(selectElement(plan.selectId));
    },
    [dispatch, elements]
  );

  /** Mid-span straight under the cursor where a Circle Junction can be branched. */
  const findPointDrawMidspanBranch = useCallback(
    (worldPt: { x: number; y: number }) => {
      const hitPerpDist = Math.max(
        gridSettings.snapGridSize * 1.25,
        28 / Math.max(viewport.zoom, 1e-6)
      );
      const rails = Object.values(elements).filter(
        (el) =>
          el.metadata.visible &&
          ALL_RAIL_TYPES.has(el.type) &&
          !pointDrawSessionIdsRef.current.has(el.id)
      );
      return findTrackNodePlacement(worldPt, rails, hitPerpDist);
    },
    [elements, gridSettings.snapGridSize, viewport.zoom]
  );

  /** Insert a Circle Junction on a mid-span straight and finish the current run. */
  const commitPointDrawMidspanBranch = useCallback(
    (worldPt: { x: number; y: number }): boolean => {
      const placement = findPointDrawMidspanBranch(worldPt);
      if (!placement) return false;

      const plan = tryInsertOnRail(
        placement.splitPoint,
        Object.values(elements),
        'circular-junction-through',
        {
          hitPerpDist: Math.max(
            gridSettings.snapGridSize,
            28 / Math.max(viewport.zoom, 1e-6)
          ),
          nextName: (type) => getNextEntityName(type),
          nextId: (type, railType) => getNextEntityId(type, railType),
          lengthMetersForWidth: (width) =>
            worldUnitsToEffectiveMeters(
              width,
              effectiveScaleFactor,
              effectiveDxfUnitsPerMeter
            ),
        }
      );
      if (!plan.ok) return false;

      const junction = plan.addElements.find((el) => el.id === plan.selectId);
      if (!junction) return false;

      const used = new Set(
        Array.isArray(junction.customProperties?.activePortIds)
          ? (junction.customProperties.activePortIds as string[])
          : []
      );
      const free = getRailEndpoints(junction)
        .map((tip, index) => ({ tip, id: `port-${index}` }))
        .find((port) => !used.has(port.id));
      if (!free) return false;

      const activePortIds = [...used, free.id];
      const connections: { id: string; fromPortId: string; toPortId: string }[] = [];
      for (const fromPortId of activePortIds) {
        for (const toPortId of activePortIds) {
          if (fromPortId !== toPortId) {
            connections.push({ id: generateId(), fromPortId, toPortId });
          }
        }
      }
      junction.customProperties = {
        ...junction.customProperties,
        activePortIds,
        connections,
      };

      commitRailInsert(plan);
      resetPointDrawRun();
      return true;
    },
    [
      commitRailInsert,
      effectiveDxfUnitsPerMeter,
      effectiveScaleFactor,
      elements,
      findPointDrawMidspanBranch,
      getNextEntityId,
      getNextEntityName,
      gridSettings.snapGridSize,
      resetPointDrawRun,
      viewport.zoom,
    ]
  );

  const commitTrackNodePlacement = useCallback(
    (worldPt: { x: number; y: number }) => {
      const plan = tryInsertTrackNodeOnRail(worldPt, Object.values(elements), {
        hitPerpDist: 40,
        nextName: getNextEntityName,
        nextId: getNextEntityId,
        lengthMetersForWidth: (width) =>
          worldUnitsToEffectiveMeters(
            width,
            effectiveScaleFactor,
            effectiveDxfUnitsPerMeter
          ),
      });
      if (!plan.ok) return false;
      commitRailInsert(plan);
      setTrackNodePreview({ x: plan.splitPoint.x, y: plan.splitPoint.y });
      return true;
    },
    [
      elements,
      getNextEntityName,
      getNextEntityId,
      effectiveScaleFactor,
      effectiveDxfUnitsPerMeter,
      commitRailInsert,
    ]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragRailMeasure(null);
      setDropPreview(null);

      const stage = stageRef.current;
      if (!stage) return;

      try {
        const itemData = e.dataTransfer.getData('application/json');
        const item = JSON.parse(itemData) as ToolboxItem;

        // Get mouse position relative to stage
        const stageBox = stage.container().getBoundingClientRect();
        const worldPt = screenToWorld(
          { x: e.clientX - stageBox.left, y: e.clientY - stageBox.top },
          { stagePos: { x: viewport.panX, y: viewport.panY }, stageScale: viewport.zoom }
        );

        const dropDefaultProperties = { ...(item.defaultProperties || {}) } as Record<string, unknown>;
        if (item.type === 'cross-rail' && dropDefaultProperties.railType === 'junction-circle') {
          // Drop onto an existing straight → split & insert (single undo step).
          const insert = tryInsertOnRail(
            worldPt,
            Object.values(elements),
            'circular-junction-through',
            {
              nextName: (type) => getNextEntityName(type),
              nextId: (type, railType) => getNextEntityId(type, railType),
              lengthMetersForWidth: (w) =>
                worldUnitsToEffectiveMeters(w, effectiveScaleFactor, effectiveDxfUnitsPerMeter),
            }
          );
          if (!('reason' in insert)) {
            commitRailInsert(insert);
            return;
          }
          // Hovering a rail but placement is illegal → reject (no free-place fallback).
          if (insert.reason !== 'not-on-rail') {
            const rect = containerRef.current?.getBoundingClientRect();
            if (rect) {
              setDragRailMeasure({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
                text: insertRejectLabel(insert.reason),
                valid: false,
              });
              window.setTimeout(() => setDragRailMeasure(null), 1600);
            }
            return;
          }
          // Empty canvas → existing port-count popover for free placement.
          setPendingCircleJunctionDrop({
            item,
            worldPt,
            uiPt: { x: e.clientX - stageBox.left, y: e.clientY - stageBox.top },
            portCount: String(dropDefaultProperties.maxPorts ?? 8),
          });
          return;
        }

        if (item.type === 'stocker' || dropDefaultProperties.objectType === 'stocker') {
          setPendingStockerDrop({
            item,
            worldPt,
            uiPt: { x: e.clientX - stageBox.left, y: e.clientY - stageBox.top },
            bayCount: String(dropDefaultProperties.bayCount ?? 4),
          });
          return;
        }

        dropToolboxItem(item, worldPt);
      } catch (error) {
        console.error('Error handling drop:', error);
      }
    },
    [
      dropToolboxItem,
      viewport.panX,
      viewport.panY,
      viewport.zoom,
      elements,
      commitRailInsert,
      getNextEntityName,
      getNextEntityId,
      effectiveScaleFactor,
      effectiveDxfUnitsPerMeter,
    ]
  );

  const handleCircleJunctionPopoverApply = useCallback(() => {
    if (!pendingCircleJunctionDrop) return;
    const sanitizeCount = (value: string, fallback: number) => {
      const n = Number(value);
      if (!Number.isFinite(n)) return fallback;
      return Math.max(1, Math.min(8, Math.round(n)));
    };
    const maxPorts = sanitizeCount(pendingCircleJunctionDrop.portCount, 8);

    dropToolboxItem(pendingCircleJunctionDrop.item, pendingCircleJunctionDrop.worldPt, {
      maxPorts,
      connections: [],
    });
    setPendingCircleJunctionDrop(null);
  }, [pendingCircleJunctionDrop, dropToolboxItem]);

  const handleStockerPopoverApply = useCallback(() => {
    if (!pendingStockerDrop) return;
    dropToolboxItem(pendingStockerDrop.item, pendingStockerDrop.worldPt, {
      bayCount: pendingStockerDrop.bayCount || '4',
    });
    setPendingStockerDrop(null);
  }, [pendingStockerDrop, dropToolboxItem]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!containerRef.current) {
      setDragRailMeasure(null);
      return;
    }

    let item: ToolboxItem | null = draggingToolboxItemRef.current;
    try {
      const itemData = e.dataTransfer.getData('application/json');
      if (itemData) item = JSON.parse(itemData) as ToolboxItem;
    } catch {
      // keep ref item
    }

    const rect = containerRef.current.getBoundingClientRect();
    const uiX = e.clientX - rect.left;
    const uiY = e.clientY - rect.top;

    // Circular Junction: live insert validity while dragging over tracks.
    if (isCircleJunctionToolboxItem(item)) {
      const worldPt = screenToWorld(
        { x: uiX, y: uiY },
        { stagePos: { x: viewport.panX, y: viewport.panY }, stageScale: viewport.zoom }
      );
      const insert = tryInsertOnRail(
        worldPt,
        Object.values(elements),
        'circular-junction-through'
      );
      if (!('reason' in insert)) {
        setDragRailMeasure({
          x: uiX,
          y: uiY,
          text:
            insert.removeIds.length > 1
              ? `Connect ${insert.removeIds.length} tracks with junction`
              : 'Insert junction on track',
          valid: true,
        });
        return;
      }
      if (insert.reason !== 'not-on-rail') {
        setDragRailMeasure({
          x: uiX,
          y: uiY,
          text: insertRejectLabel(insert.reason),
          valid: false,
        });
        return;
      }
      setDragRailMeasure({
        x: uiX,
        y: uiY,
        text: 'Drop to place junction',
        valid: true,
      });
      return;
    }

    const isRailItem = !!item && ALL_RAIL_TYPES.has(item.type);
    // Stockers ride the nearest rail on drop, so they need the same ghost as
    // tracks — otherwise the dock snap only becomes visible after release and
    // reads as the element jumping somewhere else.
    if (!item || (!isRailItem && item.type !== 'stocker')) {
      setDragRailMeasure(null);
      setDropPreview(null);
      return;
    }

    // Live ghost of the final element, snapped onto junction ports / rail ends.
    const dragWorldPt = screenToWorld(
      { x: uiX, y: uiY },
      { stagePos: { x: viewport.panX, y: viewport.panY }, stageScale: viewport.zoom }
    );
    const placement = computeToolboxDropPlacement(item, dragWorldPt, {
      elements: Object.values(elements),
      gridSettings,
      scaleFactor: effectiveScaleFactor,
      dxfUnitsPerMeter: effectiveDxfUnitsPerMeter,
      stops,
      name: item.name,
    });
    setDropPreview((prev) => (isSamePlacement(prev, placement) ? prev : placement));

    if (!isRailItem) {
      setDragRailMeasure(null);
      return;
    }

    const isStraightRail =
      item.type === 'overhead-rail' && item.defaultProperties?.railType === 'straight';
    const worldLength = isStraightRail
      ? item.defaultDimensions.width
      : Math.max(item.defaultDimensions.width, item.defaultDimensions.height);
    const lengthMeters = worldUnitsToEffectiveMeters(
      worldLength,
      effectiveScaleFactor,
      effectiveDxfUnitsPerMeter
    );
    const gridCount = worldUnitsToEffectiveGrids(worldLength);
    setDragRailMeasure({
      x: uiX,
      y: uiY,
      text: `${gridCount.toFixed(2)} grids ≈ ${formatMeasurementLabel(lengthMeters, measurementUnit)}`,
      valid: true,
    });
  };

  // Dragging back out of the canvas drops the ghost. Fires for child nodes too,
  // so ignore leaves that stay inside the container.
  const handleDragLeave = (e: React.DragEvent) => {
    const next = e.relatedTarget as Node | null;
    if (next && containerRef.current?.contains(next)) return;
    setDragRailMeasure(null);
    setDropPreview(null);
  };

  // Convert a Konva stage pointer position (screen-space) to world-space coords.
  const toWorld = (pointer: { x: number; y: number }) => ({
    ...screenToWorld(pointer, {
      stagePos: { x: viewport.panX, y: viewport.panY },
      stageScale: viewport.zoom,
    }),
  });

  // Hit-test facility zones and the floor plan under the pointer (throttled to
  // one run per frame). Zones take priority; when over a zone we suppress the
  // polyline popover so the two inspectors never stack.
  const runHoverPick = useCallback(() => {
    hoverRafRef.current = null;
    const p = pendingPointerRef.current;
    if (!p) return;
    if (effectiveTool !== 'pan' || isPanning) {
      setZoneHover((z) => (z ? null : z));
      setHover((h) => (h ? null : h));
      return;
    }
    const wx = (p.sx - viewport.panX) / viewport.zoom;
    const wy = (p.sy - viewport.panY) / viewport.zoom;

    // 1) Facility zones (highest priority).
    if (facilityVisible && facilityRegions.length > 0) {
      const region = pickRegion(
        wx,
        wy,
        facilityRegions,
        facilityVisibleTypes,
        facilityMinConfidence
      );
      if (region) {
        setZoneHover({ id: region.id, sx: p.sx, sy: p.sy });
        setHover((h) => (h ? null : h));
        return;
      }
    }
    setZoneHover((z) => (z ? null : z));

    // 2) Floor-plan polyline.
    // Raster Simulation Mode has no per-entity DXF interaction. Facility-zone
    // picking above remains active, but avoid the O(polyline count) floor scan.
    if (simulationMode || !fpVisible || fpPolylines.length === 0) {
      setHover((h) => (h ? null : h));
      return;
    }
    const threshold = 6 / viewport.zoom; // ~6px pick radius regardless of zoom
    const idx = pickPolyline(wx, wy, threshold, fpPolylines, fpBBoxes, fpPolylineLayers, fpLayers);
    if (idx < 0) {
      setHover((h) => (h ? null : h));
    } else {
      setHover({ index: idx, sx: p.sx, sy: p.sy });
    }
  }, [
    effectiveTool,
    simulationMode,
    fpVisible,
    fpPolylines,
    fpBBoxes,
    fpPolylineLayers,
    fpLayers,
    viewport.panX,
    viewport.panY,
    viewport.zoom,
    isPanning,
    facilityVisible,
    facilityRegions,
    facilityVisibleTypes,
    facilityMinConfidence,
  ]);

  const scheduleHoverPick = useCallback(
    (pointer: { x: number; y: number }) => {
      if (effectiveTool !== 'pan') return;
      pendingPointerRef.current = { sx: pointer.x, sy: pointer.y };
      if (hoverRafRef.current == null) {
        hoverRafRef.current = requestAnimationFrame(runHoverPick);
      }
    },
    [runHoverPick, effectiveTool]
  );

  const clearHover = useCallback(() => {
    if (hoverRafRef.current != null) {
      cancelAnimationFrame(hoverRafRef.current);
      hoverRafRef.current = null;
    }
    pendingPointerRef.current = null;
    setHover((h) => (h ? null : h));
    setZoneHover((z) => (z ? null : z));
    setEquipmentHover((h) => (h ? null : h));
  }, []);

  const handleCanvasMouseLeave = useCallback(() => {
    clearHover();
    // Discovery rings / preview leave with the cursor.
    setPortHints({ ports: [], activeTip: null, route: null });
    setPointDrawCenterline(null);
    setPointPreview(null);
    setTrackNodePreview(null);
  }, [clearHover]);

  useEffect(
    () => () => {
      if (hoverRafRef.current != null) cancelAnimationFrame(hoverRafRef.current);
    },
    []
  );

  useEffect(() => {
    if (effectiveTool !== 'pan') {
      clearHover();
    }
  }, [effectiveTool, clearHover]);

  // Resolve the hovered polyline into displayable entity/layer info + geometry.
  const hoverData = useMemo(() => {
    if (!hover) return null;
    const pts = fpPolylines[hover.index];
    if (!pts || pts.length < 2) return null;
    const meta = fpPolylineMeta[hover.index];
    const layerIdx = fpPolylineLayers[hover.index];
    const layer = fpLayers[layerIdx];

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (let i = 0; i + 1 < pts.length; i += 2) {
      const x = pts[i];
      const y = pts[i + 1];
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }

    return {
      sx: hover.sx,
      sy: hover.sy,
      points: pts,
      type: meta?.type ?? 'UNKNOWN',
      entityColor: meta?.color ?? 256,
      layerName: layer?.name ?? '—',
      layerColor: layer?.color ?? 7,
      category: meta?.category ?? layer?.category,
      subcategory: meta?.subcategory ?? layer?.subcategory,
      layerVisible: layer?.visible ?? true,
      frozen: layer?.frozen,
      locked: layer?.locked,
      pointCount: Math.floor(pts.length / 2),
      width: maxX - minX,
      height: maxY - minY,
      coordX: (minX + maxX) / 2,
      coordY: (minY + maxY) / 2,
      startX: pts[0],
      startY: pts[1],
      endX: pts[pts.length - 2],
      endY: pts[pts.length - 1],
    };
  }, [hover, fpPolylines, fpPolylineMeta, fpPolylineLayers, fpLayers]);

  // Lookup map id → region for hover/selection rendering.
  const regionsById = useMemo(() => {
    const m = new Map<string, (typeof facilityRegions)[number]>();
    for (const r of facilityRegions) m.set(r.id, r);
    return m;
  }, [facilityRegions]);

  const zoneHoverData = useMemo(() => {
    if (!zoneHover) return null;
    const r = regionsById.get(zoneHover.id);
    if (!r) return null;
    return { region: r, sx: zoneHover.sx, sy: zoneHover.sy };
  }, [zoneHover, regionsById]);

  const equipmentHoverData = useMemo(() => {
    if (!equipmentHover) return null;
    const inst = eqInstances[equipmentHover.instanceId];
    if (!inst) return null;
    const template = inst.templateId ? eqTemplates[inst.templateId] : null;
    const style = resolveStyle(inst, template);
    const ports = resolvePorts(inst, template);
    const bounds = polygonBounds(inst.polygon);
    const centroid = polygonCentroid(inst.polygon);
    return {
      sx: equipmentHover.sx,
      sy: equipmentHover.sy,
      name: inst.name,
      objectType: inst.objectType,
      category: inst.category,
      process:
        typeof inst.overrides.properties?.process === 'string'
          ? inst.overrides.properties.process
          : null,
      description: inst.description,
      tags: inst.tags,
      templateName: template?.name ?? null,
      rotation: inst.rotation,
      portCount:
        equipmentProvenance(inst) === 'layout' ? ports.length : 0,
      notes: inst.notes,
      color: style.color,
      coordX: centroid.x,
      coordY: centroid.y,
      width: bounds.maxX - bounds.minX,
      height: bounds.maxY - bounds.minY,
      sourceLabel: EQUIPMENT_PROVENANCE_LABEL[equipmentProvenance(inst)],
    };
  }, [equipmentHover, eqInstances, eqTemplates]);

  const selectedRegion = useMemo(
    () => (facilitySelectedId ? (regionsById.get(facilitySelectedId) ?? null) : null),
    [facilitySelectedId, regionsById]
  );

  // When a new region is selected (e.g. from the panel), pan to centre it if it
  // isn't already comfortably within the viewport. Keeps the current zoom.
  const lastFocusedRegionRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedRegion) {
      lastFocusedRegionRef.current = null;
      return;
    }
    if (lastFocusedRegionRef.current === selectedRegion.id) return;
    lastFocusedRegionRef.current = selectedRegion.id;
    if (stageSize.width === 0) return;

    const cx = selectedRegion.x + selectedRegion.width / 2;
    const cy = selectedRegion.y + selectedRegion.height / 2;
    const screenX = cx * viewport.zoom + viewport.panX;
    const screenY = cy * viewport.zoom + viewport.panY;
    const margin = 60;
    const outside =
      screenX < margin ||
      screenX > stageSize.width - margin ||
      screenY < margin ||
      screenY > stageSize.height - margin;
    if (outside) {
      setViewport({
        panX: stageSize.width / 2 - cx * viewport.zoom,
        panY: stageSize.height / 2 - cy * viewport.zoom,
      });
    }
  }, [selectedRegion, viewport.zoom, viewport.panX, viewport.panY, stageSize, setViewport]);

  // Rubber-band selection — only on empty canvas in select mode
  const handleElementContextMenu = useCallback(
    (coords: { clientX: number; clientY: number }, elementId: string) => {
      if (wasRightClickPannedRef.current) return;
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      if (!elements[elementId]) return;

      if (!selectedIds.includes(elementId)) {
        dispatch(selectElement(elementId));
      }

      setElementContextMenu({
        x: coords.clientX - rect.left,
        y: coords.clientY - rect.top,
        elementId,
      });
    },
    [elements, selectedIds, dispatch]
  );

  const handleStageMouseDown = (e: any) => {
    if (pointDraw.active) return; // point-draw tool owns canvas clicks
    if (eqDraw.active) return; // equipment tool owns canvas clicks
    if (trackNodePlacementActive) return; // track-node tool owns canvas clicks
    if (routeEndpointPlacement) return; // route start/end capture owns canvas clicks
    if (effectiveTool !== 'select') return;
    if (e.target !== stageRef.current) return; // element click handled by element
    const pointer = stageRef.current?.getPointerPosition();
    if (!pointer) return;
    const wp = toWorld(pointer);
    isRubberBandingRef.current = true;
    selectionStartRef.current = wp;
    const emptyRect = { x: wp.x, y: wp.y, width: 0, height: 0 };
    selectionRectRef.current = emptyRect;
    setSelectionRect(emptyRect);
    setSelectedLockedLayerId(null);
    dispatch(clearSelection());
    dispatch(selectTrackNode(null));
    dispatch(selectInstance(null));
    dispatch(setEquipmentReviewSelection([]));
  };

  const handleStageMouseMove = () => {
    const pointer = stageRef.current?.getPointerPosition();

    // While Point Draw is active, track a snapped cursor for the rubber-band
    // segment preview, and detect nearby rotary-junction ports to highlight and
    // preview a tangent connection toward the snapped port.
    if (pointDraw.active) {
      if (!pointer) {
        setPointPreview(null);
        setPortHints({ ports: [], activeTip: null, route: null });
        setPointDrawCenterline(null);
        return;
      }
      const worldPointer = toWorld(pointer);
      const snap = findPortSnap(worldPointer);
      const head = pointDraw.vertices[pointDraw.vertices.length - 1] ?? null;

      let route: number[] | null = null;
      // Always advertise the closest legal tip in the discovery radius; promote to
      // the snap target when a tangent route is previewed (snapping unchanged).
      let activeTip: { x: number; y: number } | null = snap.closestTip;
      if (snap.target && head) {
        const wps = routeHeadToPort(head, snap.target.arrivalHeading, snap.target.tip);
        if (wps) {
          route = [head.x, head.y];
          for (const w of wps) route.push(w.x, w.y);
          activeTip = snap.target.tip;
        }
      }

      // Advertise a nearby turn corner with the same ring ports use, so the
      // convert-to-junction target is visible instead of guessed.
      const turn = findTurnSnap(worldPointer);
      let ports = turn
        ? [...snap.ports, { x: turn.hit.corner.x, y: turn.hit.corner.y, legal: true }]
        : [...snap.ports];
      if (turn?.withinSnap && !snap.target) activeTip = turn.hit.corner;

      // Mid-span branch target (Circle Junction insert). Prefer it over distant
      // end-node highlights so users can start a T from the middle of a track.
      const midspan = findPointDrawMidspanBranch(worldPointer);
      if (midspan) {
        const split = midspan.splitPoint;
        ports = [...ports, { x: split.x, y: split.y, legal: true }];
        setPointDrawCenterline(split);
        const midDist = Math.hypot(split.x - worldPointer.x, split.y - worldPointer.y);
        const tipDist = activeTip
          ? Math.hypot(activeTip.x - worldPointer.x, activeTip.y - worldPointer.y)
          : Infinity;
        if (!head || midDist <= tipDist) {
          activeTip = split;
          if (!head) route = null;
        }
      } else {
        setPointDrawCenterline(null);
      }

      setPortHints({ ports, activeTip, route });

      // When a tangent route is previewed, suppress the free H/V rubber-band.
      // Occupied-track suppression only applies when auto-junction insertion
      // is not applicable for the candidate leg.
      let blocked =
        !snap.target && !midspan && isPointDrawBlockedByTrack(worldPointer);
      if (blocked && head) {
        const candidate = snapPointDrawPoint(worldPointer);
        const travelHeading =
          lastPointStraightRef.current?.heading ??
          (pointDraw.vertices.length >= 2
            ? segHeading(
                pointDraw.vertices[pointDraw.vertices.length - 2],
                pointDraw.vertices[pointDraw.vertices.length - 1]
              )
            : null);
        const inboundStraightLength =
          lastPointStraightRef.current?.width ??
          (pointDraw.vertices.length >= 2
            ? Math.hypot(
                pointDraw.vertices[pointDraw.vertices.length - 1].x -
                  pointDraw.vertices[pointDraw.vertices.length - 2].x,
                pointDraw.vertices[pointDraw.vertices.length - 1].y -
                  pointDraw.vertices[pointDraw.vertices.length - 2].y
              )
            : null);
        const solution = solvePointDrawRoute(
          {
            head,
            travelHeading,
            cursor: candidate,
            radius: segmentSizing.radiusUnits,
            minStraight: segmentSizing.radiusUnits,
            inboundStraightLength,
          },
          segmentSizing
        );
        const pullTolerance = Math.max(
          gridSettings.snapGridSize * 1.5,
          28 / Math.max(viewport.zoom, 1e-6)
        );
        if (
          solution.valid &&
          solution.pattern === 'straight' &&
          findPerpendicularStraightCrossing(
            head,
            solution.endPoint,
            Object.values(elements),
            pointDrawSessionIdsRef.current,
            { pullTolerance }
          )
        ) {
          blocked = false;
        } else if (
          findPerpendicularStraightCrossing(
            head,
            dominantAxisEnd(head, candidate).end,
            Object.values(elements),
            pointDrawSessionIdsRef.current,
            { pullTolerance }
          )
        ) {
          blocked = false;
        } else if (
          turn?.withinSnap &&
          planPointDrawCurveJunctionInsert(
            turn.hit.host,
            head,
            Object.values(elements)
          ).ok
        ) {
          blocked = false;
        }
      }
      const midspanIsActive =
        !!midspan &&
        !!activeTip &&
        Math.hypot(activeTip.x - midspan.splitPoint.x, activeTip.y - midspan.splitPoint.y) <
          1e-6;
      setPointPreview(
        route || blocked || midspanIsActive ? null : snapPointDrawPoint(worldPointer)
      );
      return;
    }

    // While drawing equipment, track a snapped cursor for the live preview.
    if (eqDraw.active) {
      if (pointer) setEqCursor(snapEquipmentPoint(toWorld(pointer)));
      else setEqCursor(null);
      return;
    }

    if (trackNodePlacementActive) {
      if (!pointer) {
        setTrackNodePreview(null);
        return;
      }
      const placement = findTrackNodePlacement(
        toWorld(pointer),
        Object.values(elements),
        40
      );
      setTrackNodePreview(
        placement
          ? { x: placement.splitPoint.x, y: placement.splitPoint.y }
          : null
      );
      return;
    }

    // While placing a stop, snap a live preview marker onto the nearest rail.
    if (placingStopForVehicle) {
      if (!pointer) {
        setStopPreview(null);
        return;
      }
      const wp = toWorld(pointer);
      const proj = projectPointToNearestRail(Object.values(elements), wp, 40);
      setStopPreview(proj ? { x: proj.x, y: proj.y } : null);
      return;
    }

    // While rubber-band selecting, update the selection rectangle and skip hover.
    if (isRubberBandingRef.current && selectionStartRef.current) {
      if (!pointer) return;
      const wp = toWorld(pointer);
      const start = selectionStartRef.current;
      const nextRect = {
        x: Math.min(start.x, wp.x),
        y: Math.min(start.y, wp.y),
        width: Math.abs(wp.x - start.x),
        height: Math.abs(wp.y - start.y),
      };
      selectionRectRef.current = nextRect;
      setSelectionRect(nextRect);
      return;
    }

    // Skip expensive polyline hit-tests while the viewport is moving.
    if (
      isPanning ||
      isRightClickPanningRef.current ||
      viewportGestureRef.current
    ) {
      clearHover();
      return;
    }

    if (pointer) scheduleHoverPick(pointer);
    else clearHover();
  };

  const handleStageMouseUp = () => {
    if (!isRubberBandingRef.current) return;
    isRubberBandingRef.current = false;

    const rect = selectionRectRef.current;
    selectionRectRef.current = null;
    setSelectionRect(null);
    selectionStartRef.current = null;

    if (!rect || (rect.width < 4 && rect.height < 4)) return;

    // Konva still emits a click after mouseup — don't let it wipe this selection.
    suppressNextStageClickRef.current = true;

    const rectMaxX = rect.x + rect.width;
    const rectMaxY = rect.y + rect.height;
    const overlaps = (box: { minX: number; minY: number; maxX: number; maxY: number }) =>
      box.minX < rectMaxX &&
      box.maxX > rect.x &&
      box.minY < rectMaxY &&
      box.maxY > rect.y;

    const ids = Object.values(elements)
      .filter((el) => {
        if (!el.metadata.visible) return false;
        const layerId = el.layerId || el.metadata.layerId;
        const layer = layerId ? layoutLayers[layerId] : null;
        if (layer && !layer.visible) return false;
        // Locked layers are selected as a unit via their wrapper below.
        if (layer?.locked) return false;
        return overlaps(getAABB(el));
      })
      .map((el) => el.id);

    if (ids.length > 0) dispatch(selectElements(ids));

    const equipmentIds = eqInstanceOrder.filter((id) => {
      const instance = eqInstances[id];
      if (!instance) return false;
      if (
        !isEquipmentOverlayVisible(instance, {
          overlayVisible: eqVisible,
          dxfLayers: fpLayers,
          hiddenEntityTypes: fpHiddenEntityTypes,
          hiddenProcesses: eqHiddenProcesses,
          layoutElements: elements,
        })
      ) {
        return false;
      }
      return overlaps(polygonBounds(worldPolygon(instance)));
    });
    const reviewIds = equipmentIds.filter(
      (id) => equipmentReviewStatus(eqInstances[id]) === 'potential'
    );
    const selectedEquipmentIds = equipmentIds.filter(
      (id) => equipmentReviewStatus(eqInstances[id]) !== 'potential'
    );
    if (selectedEquipmentIds.length > 0) dispatch(selectInstances(selectedEquipmentIds));
    if (reviewIds.length > 0) dispatch(setEquipmentReviewSelection(reviewIds));

    // Marquee over a locked layer wrapper selects that layer (same as clicking it).
    const hitLocked = lockedLayerWrappers.find((wrapper) =>
      overlaps({
        minX: wrapper.x,
        minY: wrapper.y,
        maxX: wrapper.x + wrapper.width,
        maxY: wrapper.y + wrapper.height,
      })
    );
    setSelectedLockedLayerId(hitLocked?.layerId ?? null);
  };

  // Handle canvas click (deselect) — skip if we just finished a rubber-band
  const handleStageClick = (e: any) => {
    setElementContextMenu(null);
    setEquipmentContextMenu(null);
    if (suppressNextStageClickRef.current) {
      suppressNextStageClickRef.current = false;
      return;
    }
    if (isRubberBandingRef.current) return;

    // Equipment drawing tool consumes clicks to build a footprint.
    if (eqDraw.active) {
      const pointer = stageRef.current?.getPointerPosition();
      if (pointer) handleEquipmentDrawClick(toWorld(pointer));
      return;
    }

    // Point Draw: the first click sets the start point; each subsequent click
    // commits a constraint-legal run (horizontal/vertical straight, with a
    // fixed-radius corner curve inserted on a 90° turn) and advances the start
    // to the new endpoint. Enter/Escape end the run (handled via keyboard).
    if (pointDraw.active) {
      const pointer = stageRef.current?.getPointerPosition();
      if (!pointer) return;
      const worldPointer = toWorld(pointer);
      const verts = pointDraw.vertices;
      const last = verts[verts.length - 1];

      // Point Draw owns every click. Resolve it centrally by geometry, in strict
      // priority order — junction ports → logical open ends → perpendicular
      // auto-junction → occupied rejection → empty grid — so existing elements
      // can never steal the click for selection.
      const portSnap = findPortSnap(worldPointer);

      if (!last) {
        // First click priority:
        //   1) curve turn → Circle Junction
        //   2) mid-span straight → Circle Junction (preferred over
        //      distant end-node snap so T-junctions can start from track center)
        //   3) open end / junction port
        //   4) empty grid
        lastPointStraightRef.current = null;
        firstPointStraightRef.current = null;
        pointDrawSessionIdsRef.current.clear();

        const firstTurn = findTurnSnap(worldPointer);
        if (firstTurn?.withinSnap) {
          const plan = planCircularJunctionInsertOnCurve(
            firstTurn.hit.host,
            Object.values(elements),
            {
              nextName: (type) => getNextEntityName(type),
              nextId: (type, railType) => getNextEntityId(type, railType),
              lengthMetersForWidth: (width) =>
                worldUnitsToEffectiveMeters(
                  width,
                  effectiveScaleFactor,
                  effectiveDxfUnitsPerMeter
                ),
            }
          );
          if (plan.ok) {
            commitRailInsert(plan);
            resetPointDrawRun();
            return;
          }
        }

        const midspan = findPointDrawMidspanBranch(worldPointer);
        // The tip actually pointed at, not the cardinal-preferred one: a run may
        // legitimately start on any spoke, including a corner.
        const tip = portSnap.nearest?.tip ?? null;
        const midDist = midspan
          ? Math.hypot(
              midspan.splitPoint.x - worldPointer.x,
              midspan.splitPoint.y - worldPointer.y
            )
          : Infinity;
        const tipDist = tip
          ? Math.hypot(tip.x - worldPointer.x, tip.y - worldPointer.y)
          : Infinity;

        if (midspan && midDist <= tipDist) {
          if (commitPointDrawMidspanBranch(worldPointer)) return;
          // Insert refused (too short / near junction) — fall through.
        }

        if (tip) {
          dispatch(addPointDrawVertex(tip));
          return;
        }

        // Still on occupied mid-track but insert failed — don't stack geometry.
        if (isPointDrawBlockedByTrack(worldPointer)) return;

        dispatch(addPointDrawVertex(snapPointDrawPoint(worldPointer)));
        return;
      }

      const approachMidspan = findPointDrawMidspanBranch(worldPointer);
      // Measured from the pointed-at tip so the mid-span / closure comparisons
      // reflect how near the hub really is, not how far the preferred spoke sits.
      const approachTip = portSnap.nearest?.tip ?? null;
      const approachMidDist = approachMidspan
        ? Math.hypot(
            approachMidspan.splitPoint.x - worldPointer.x,
            approachMidspan.splitPoint.y - worldPointer.y
          )
        : Infinity;
      const approachTipDist = approachTip
        ? Math.hypot(approachTip.x - worldPointer.x, approachTip.y - worldPointer.y)
        : Infinity;
      const preferMidspanJoin =
        !!approachMidspan && approachMidDist <= approachTipDist;

      // Corner the click would close the loop onto. Corners after the run's
      // start count too, so a loop branched off existing track closes where it
      // actually meets itself rather than at the far end of its lead-in stub.
      const closeDist = Math.max(gridSettings.snapGridSize, 12 / viewport.zoom);
      const closeAnchor = resolvePointDrawCloseAnchor(verts, worldPointer, closeDist);
      const closeAnchorDist = closeAnchor
        ? Math.hypot(
            closeAnchor.point.x - worldPointer.x,
            closeAnchor.point.y - worldPointer.y
          )
        : Infinity;
      // A loop drawn back to its own start often finishes right beside the port
      // the run branched from. Closing is the nearer intent there, and the
      // overlay ripple has been naming that corner, so it must not lose the
      // click to a tangent connection the operator never aimed at.
      const preferClose = closeAnchorDist <= Math.min(approachTipDist, approachMidDist);

      // 1. Junction port / logical open end → auto-route a tangent connection.
      // Skipped when the pointer is closer to a mid-span branch target so a
      // Circle Junction T-in wins over a distant end-node snap.
      if (
        !preferMidspanJoin &&
        !preferClose &&
        connectHeadToPort(last, portSnap.targets)
      ) {
        setPortHints({ ports: [], activeTip: null, route: null });
        return;
      }

      // 2. Intent to close: snap the loop shut legally.
      if (closeAnchor && closePointDrawLoop(closeAnchor)) return;

      // 3. Turn corner → convert the curve into a Circular Junction and T-in.
      // Runs before the crossing check: near a corner the adjacent straights are
      // also under the cursor, and the crossing planner would claim the click.
      const curveResult = commitPointDrawCurveJunction(last, worldPointer);
      if (curveResult === 'committed' || curveResult === 'rejected') return;

      // 4. Occupied / crossing straight: try automatic Circular Junction first.
      // Only reject the occupied centerline when no valid insertion applies.
      const target = preferMidspanJoin
        ? approachMidspan!.splitPoint
        : snapPointDrawPoint(worldPointer);
      const crossingResult = commitPointDrawCrossing(last, target, verts);
      if (crossingResult === 'committed' || crossingResult === 'rejected') return;

      // Mid-span preferred but crossing failed — don't fall back to end snap/stack.
      if (preferMidspanJoin) return;

      // End snap after mid-span path declined.
      if (connectHeadToPort(last, portSnap.targets)) {
        setPortHints({ ports: [], activeTip: null, route: null });
        return;
      }

      // 5. Occupied mid-track with no insert strategy → reject (no stacking).
      if (isPointDrawBlockedByTrack(worldPointer)) return;

      // 6. Empty space → pattern-based route (straight / turn90 / U-turn).
      const appended = commitPointDrawClick(last, target, verts);
      if (appended) {
        for (const v of appended) dispatch(addPointDrawVertex(v));
      }
      return;
    }

    if (trackNodePlacementActive) {
      const pointer = stageRef.current?.getPointerPosition();
      if (pointer) commitTrackNodePlacement(toWorld(pointer));
      return;
    }

    // LLM-route start/end capture: first click sets START, second sets END, then resolve.
    if (routeEndpointPlacement) {
      const pointer = stageRef.current?.getPointerPosition();
      if (pointer) {
        const world = toWorld(pointer);
        if (routeEndpointPlacement === 'start') {
          dispatch(setRouteStartPoint(world));
          dispatch(setRouteEndpointPlacement('end'));
        } else {
          dispatch(setRouteEndPoint(world));
          dispatch(setRouteEndpointPlacement(null));
          if (routeStartPoint) {
            completeEndpointCapture({ start: routeStartPoint, end: world });
          }
        }
      }
      return;
    }

    const clickedOnEmpty = e.target === e.target.getStage();

    // Left-click empty canvas hides equipment overlays.
    // if (clickedOnEmpty) {
    //   dispatch(setEquipmentVisible(false));
    // }

    // In stop-placement mode, a click near a rail drops the stop there; a click
    // away from any track cancels placement. (Direct rail hits are handled by
    // CanvasElement, which stops event bubbling, so this only runs for misses.)
    if (placingStopForVehicle) {
      const pointer = stageRef.current?.getPointerPosition();
      if (pointer) {
        const worldPos = toWorld(pointer);
        const stockers = Object.values(elements).filter((el) => el.type === 'stocker');
        let closestStocker = null;
        let minStockerDist = Infinity;
        for (const s of stockers) {
          const trayPos = getStockerTrayPosition(s);
          const dist = Math.hypot(worldPos.x - trayPos.x, worldPos.y - trayPos.y);
          if (dist < minStockerDist) {
            minStockerDist = dist;
            closestStocker = s;
          }
        }

        let projPos = worldPos;
        if (closestStocker && minStockerDist < 50) {
          projPos = getStockerTrayPosition(closestStocker);
        }

        const proj = projectPointToNearestRail(Object.values(elements), projPos, 40);
        if (proj) {
          dispatch(
            addVehicleStop({
              vehicleId: placingStopForVehicle,
              stop: { railId: proj.railId, distance: proj.distance, durationS: 2 },
            })
          );
        }
      }
      dispatch(setPlacingStopForVehicle(null));
      return;
    }

    if (!clickedOnEmpty || effectiveTool !== 'select') return;

    setSelectedLockedLayerId(null);

    // Clicking a facility zone selects it (zones are a non-listening overlay, so
    // their clicks fall through to the stage and we resolve them by hit-test).
    if (facilityVisible && facilityRegions.length > 0) {
      const pointer = stageRef.current?.getPointerPosition();
      if (pointer) {
        const wp = toWorld(pointer);
        const region = pickRegion(
          wp.x,
          wp.y,
          facilityRegions,
          facilityVisibleTypes,
          facilityMinConfidence
        );
        if (region) {
          dispatch(selectRegion(region.id));
          dispatch(clearSelection());
          dispatch(selectInstance(null));
          dispatch(selectTrackNode(null));
          return;
        }
      }
    }

    // Click near a floor-plan polyline to focus it in the layers panel.
    if (fpVisible && fpPolylines.length > 0) {
      const pointer = stageRef.current?.getPointerPosition();
      if (pointer) {
        const wx = (pointer.x - viewport.panX) / viewport.zoom;
        const wy = (pointer.y - viewport.panY) / viewport.zoom;
        const threshold = 6 / viewport.zoom;
        const idx = pickPolyline(
          wx,
          wy,
          threshold,
          fpPolylines,
          fpBBoxes,
          fpPolylineLayers,
          fpLayers
        );
        if (idx >= 0) {
          dispatch(setFocusedPolyline(idx));
          dispatch(setDxfLayersPanelOpen(true));
          return;
        }
      }
    }

    dispatch(clearSelection());
    dispatch(selectTrackNode(null));
    dispatch(selectInstance(null));
    if (facilitySelectedId) dispatch(selectRegion(null));
  };

  // Handle wheel zoom
  const handleWheel = (e: any) => {
    e.evt.preventDefault();
    if (!viewportGestureRef.current) {
      viewportGestureRef.current = true;
      setViewportGestureActive(true);
    }

    const stage = stageRef.current;
    if (!stage) return;

    const pending = pendingWheelViewportRef.current;
    const oldScale = pending?.zoom ?? stage.scaleX() ?? viewport.zoom;
    const oldPanX = pending?.panX ?? stage.x();
    const oldPanY = pending?.panY ?? stage.y();
    const pointer = stage.getPointerPosition();

    if (!pointer) return;

    const nextViewport = computeWheelZoomViewport(
      { zoom: oldScale, panX: oldPanX, panY: oldPanY },
      pointer,
      e.evt.deltaY,
      MIN_ZOOM,
      MAX_ZOOM
    );
    pendingWheelViewportRef.current = nextViewport;

    // Wheel devices can emit far faster than the display. Apply at most one
    // Konva redraw per animation frame and avoid reconciling the full Canvas
    // React tree until the gesture has settled.
    if (wheelRafRef.current == null) {
      wheelRafRef.current = requestAnimationFrame(() => {
        wheelRafRef.current = null;
        const next = pendingWheelViewportRef.current;
        const liveStage = stageRef.current;
        if (!next || !liveStage) return;
        liveStage.scale({ x: next.zoom, y: next.zoom });
        liveStage.position({ x: next.panX, y: next.panY });
        liveStage.batchDraw();
      });
    }
    if (wheelSettleTimerRef.current) clearTimeout(wheelSettleTimerRef.current);
    wheelSettleTimerRef.current = setTimeout(() => {
      wheelSettleTimerRef.current = null;
      const finalViewport = pendingWheelViewportRef.current;
      pendingWheelViewportRef.current = null;
      viewportGestureRef.current = false;
      setViewportGestureActive(false);
      if (!finalViewport) return;
      setViewport(finalViewport);
      scheduleZoomReduxSync(finalViewport.zoom);
    }, 90);
  };

  // Handle stage drag (pan)
  const handleStageDragStart = (e: any) => {
    // Ignore drags that originate from a child element; only react to the stage
    // panning itself. Otherwise dragging an element would be treated as a pan.
    if (e.target !== stageRef.current) return;
    if (effectiveTool === 'pan') {
      setIsPanning(true);
    }
  };

  const handleStageDragEnd = (e: any) => {
    // Element drags bubble up to the stage's onDragEnd. Without this guard we
    // would read the element's coordinates and slam them onto the stage
    // position, making the view jump away when placing/moving objects.
    if (e.target !== stageRef.current) return;
    setViewport({
      panX: e.target.x(),
      panY: e.target.y(),
    });
    setIsPanning(false);
  };

  // Persistent markers for every manually-placed stop, shown on the canvas so
  // the user can see them whether or not the simulation is running.
  const placedStopMarkers = Object.entries(vehicleControls).flatMap(([vehicleId, control]) => {
    const oht = elements[vehicleId];
    const color = oht?.metadata.color || '#f59e0b';
    const ohtName = oht?.metadata.name || 'Unknown OHT';
    return (control.stops ?? []).map((stop, idx) => {
      const rail = elements[stop.railId];
      if (!rail) return null;
      const pos = railStopToWorld(rail, stop.distance);
      const stopName = stop.name || `Stop ${idx + 1}`;
      return { key: `${vehicleId}-${stop.id}`, x: pos.x, y: pos.y, idx: idx + 1, color, ohtName, stopName };
    });
  });

  // Point Draw live preview: same pattern solver as commit (straight / turn90 /
  // Curve180 / composed U-turn).
  const pdVerts = pointDraw.vertices;
  const pointDrawStart = pointDraw.active
    ? pdVerts[pdVerts.length - 1] ?? null
    : null;
  let pointDrawPreview:
    | {
        paths: number[][];
        endPoint: { x: number; y: number };
        valid: boolean;
      }
    | null = null;
  if (pointDrawStart && pointPreview) {
    const prevRef = lastPointStraightRef.current;
    const travelHeading =
      prevRef?.heading ??
      (pdVerts.length >= 2
        ? spokeSegHeading(pdVerts[pdVerts.length - 2], pdVerts[pdVerts.length - 1])
        : null);

    // Preview spoke-aligned first leg when starting on a junction port.
    if (travelHeading == null && pdVerts.length === 1) {
      const spokeOut = findJunctionSpokeAt(pointDrawStart);
      if (spokeOut != null) {
        const end = projectOntoSpokeRay(pointDrawStart, spokeOut, pointPreview);
        if (end && Math.hypot(end.x - pointDrawStart.x, end.y - pointDrawStart.y) > 1e-6) {
          pointDrawPreview = {
            paths: [[pointDrawStart.x, pointDrawStart.y, end.x, end.y]],
            endPoint: end,
            valid: true,
          };
        } else {
          pointDrawPreview = {
            paths: [],
            endPoint: pointDrawStart,
            valid: false,
          };
        }
      }
    }

    // Preview sharp cardinal continuation after a diagonal spoke leg.
    if (!pointDrawPreview && travelHeading != null && !isCardinalDeg(travelHeading)) {
      const { end } = dominantAxisEnd(pointDrawStart, pointPreview);
      if (Math.hypot(end.x - pointDrawStart.x, end.y - pointDrawStart.y) > 1e-6) {
        pointDrawPreview = {
          paths: [[pointDrawStart.x, pointDrawStart.y, end.x, end.y]],
          endPoint: end,
          valid: true,
        };
      } else {
        pointDrawPreview = {
          paths: [],
          endPoint: pointDrawStart,
          valid: false,
        };
      }
    }

    if (!pointDrawPreview) {
      const inboundStraightLength =
        prevRef?.width ??
        (pdVerts.length >= 2
          ? Math.hypot(
              pdVerts[pdVerts.length - 1].x - pdVerts[pdVerts.length - 2].x,
              pdVerts[pdVerts.length - 1].y - pdVerts[pdVerts.length - 2].y
            )
          : null);
      const R = segmentSizing.radiusUnits;
      const solution = solvePointDrawRoute(
        {
          head: pointDrawStart,
          travelHeading,
          cursor: pointPreview,
          radius: R,
          minStraight: R,
          inboundStraightLength,
        },
        segmentSizing
      );
      if (solution.valid) {
        // The solver only judges the next leg. Anything other than a collinear
        // straight makes the commit rebuild the whole run, which can still fail —
        // mirror that here so the preview never promises a click that no-ops.
        const rebuilds =
          solution.pattern !== 'straight' || solution.appendedVertices.length > 1;
        const commitFits =
          !rebuilds ||
          !!planRunRebuild([...pdVerts, ...solution.appendedVertices], false);
        pointDrawPreview = {
          paths: solution.previewPaths,
          endPoint: solution.endPoint,
          valid: commitFits,
        };
      } else {
        pointDrawPreview = {
          paths: [],
          endPoint: pointDrawStart,
          valid: false,
        };
      }
    }
  }

  // Loop-closing affordance: when the run has enough vertices and the cursor
  // nears a corner it could close onto, surface the animated "close" ripple on
  // that corner. The ripple tracks the resolved anchor rather than the run's
  // first vertex, so it names the point the click will actually snap to.
  // Slightly more generous than the commit threshold so the affordance appears
  // before the click becomes a closure.
  let pointDrawCloseIntent: { start: { x: number; y: number }; active: boolean } | null = null;
  if (pointDraw.active && pdVerts.length >= 3) {
    const ref = pointPreview ?? pointDrawStart;
    const closeDist = Math.max(gridSettings.snapGridSize * 1.5, 18 / viewport.zoom);
    const anchor = ref ? resolvePointDrawCloseAnchor(pdVerts, ref, closeDist) : null;
    pointDrawCloseIntent = { start: anchor?.point ?? pdVerts[0], active: !!anchor };
  }

  // Grid point the next click lands on (crosshair anchor). Falls back to the raw
  // snapped cursor before the first vertex exists.
  const pointDrawSnapPoint = pointDrawPreview
    ? pointDrawPreview.endPoint
    : pointDraw.active
      ? pointPreview
      : null;

  return (
    <div
      ref={containerRef}
      onPointerDown={handleContainerPointerDown}
      onContextMenu={(e) => {
        if (wasRightClickPannedRef.current) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      className={`w-full h-full relative bg-white ${
        placingStopForVehicle ||
        pointDraw.active ||
        eqDraw.active ||
        trackNodePlacementActive ||
        routeEndpointPlacement
          ? 'cursor-crosshair'
          : effectiveTool === 'pan' || isRightClickPanningState
            ? isPanning || isRightClickPanningState
              ? 'cursor-grabbing'
              : 'cursor-grab'
            : isRightClickPanningState
              ? 'cursor-grabbing'
              : 'cursor-default'
      }`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onMouseLeave={handleCanvasMouseLeave}
    >
      {pointDraw.active && (
        <div
          className={`absolute top-4 z-20 pointer-events-none px-3 py-2 rounded-lg bg-white/95 border border-emerald-200 text-slate-700 shadow-lg text-xs max-w-[240px] ${
            leftSidebarOpen ? 'left-[136px]' : 'left-4'
          }`}
        >
          <div className="font-semibold mb-1 flex items-center gap-1.5 text-emerald-700">
            <span className="inline-block w-2.5 h-2.5 bg-emerald-100 border border-emerald-400 rounded-full" />
            Point Draw
          </div>
          <div className="text-[11px] leading-snug text-slate-500">
            Click grid points to route track. Runs stay{' '}
            <span className="font-semibold">horizontal / vertical</span>; turns
            round to fixed <span className="font-semibold">90° curves</span>.
            Circle-junction ports also accept{' '}
            <span className="font-semibold">spoke / diagonal</span> legs.
            <br />
            Click near the <span className="font-semibold">start</span> to close
            the loop, or near a{' '}
            <span className="font-semibold text-emerald-600">junction port / open end</span>{' '}
            to connect or continue.
            <br />
            <span className="font-semibold">Enter</span> finishes ·{' '}
            <span className="font-semibold">Esc</span> cancels
          </div>
          {pointDrawPreview && !pointDrawPreview.valid && (
            <div className="mt-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] leading-snug text-amber-800">
              No legal track fits here — clicking will not place a point. Move
              further from the last point, or approach from another direction.
            </div>
          )}
        </div>
      )}

      {eqDraw.active && (
        <div
          className={`absolute top-4 z-20 pointer-events-none px-3 py-2.5 rounded-lg bg-white border border-blue-300 text-slate-800 shadow-lg text-xs max-w-[260px] ${
            leftSidebarOpen ? 'left-[136px]' : 'left-4'
          }`}
        >
          <div className="font-semibold mb-1 flex items-center gap-1.5 text-blue-800">
            <span className="inline-block w-2.5 h-2.5 bg-blue-100 border border-blue-500 rounded-[2px]" />
            Identify Equipment
          </div>
          <div className="mb-1 text-slate-800">
            Mode:{' '}
            <span className="font-semibold text-blue-800 capitalize">{eqDraw.mode}</span>
          </div>
          <div className="text-[11px] leading-snug text-slate-700">
            {eqDraw.mode === 'rectangle'
              ? 'Click two opposite corners to define the footprint.'
              : 'Click to add vertices · click the first point or double-click to finish.'}
            <br />
            <span className="font-semibold text-slate-900">Esc</span> cancels
          </div>
        </div>
      )}

      {trackNodePlacementActive && (
        <div
          className={`absolute top-4 z-20 pointer-events-none px-3 py-2 rounded-lg bg-white/95 border border-yellow-300 text-slate-700 shadow-lg text-xs max-w-[240px] ${
            leftSidebarOpen ? 'left-[136px]' : 'left-4'
          }`}
        >
          <div className="font-semibold mb-1 flex items-center gap-1.5 text-yellow-700">
            <span className="inline-block size-2.5 rounded-full bg-yellow-300 border border-yellow-600" />
            Add Track Node
          </div>
          <div className="text-[11px] leading-snug text-slate-500">
            Move over a straight track to preview a node, then click to add it.
            <br />
            <span className="font-semibold">Esc</span> exits
          </div>
        </div>
      )}

      {routeEndpointPlacement && (
        <div
          className={`absolute top-4 z-20 pointer-events-none px-3 py-2 rounded-lg bg-white/95 border shadow-lg text-xs max-w-[240px] ${
            leftSidebarOpen ? 'left-[136px]' : 'left-4'
          } ${routeEndpointPlacement === 'start' ? 'border-green-300' : 'border-red-300'}`}
        >
          <div
            className={`font-semibold mb-1 flex items-center gap-1.5 ${
              routeEndpointPlacement === 'start' ? 'text-green-700' : 'text-red-700'
            }`}
          >
            <span
              className={`inline-block size-2.5 rounded-full border ${
                routeEndpointPlacement === 'start'
                  ? 'bg-green-400 border-green-600'
                  : 'bg-red-400 border-red-600'
              }`}
            />
            {routeEndpointPlacement === 'start' ? 'Click the START point' : 'Click the END point'}
          </div>
          <div className="text-[11px] leading-snug text-slate-500">
            The route will be forced to reach this point.
            <br />
            <span className="font-semibold">Esc</span> cancels
          </div>
        </div>
      )}

      {dragRailMeasure && (
        <div
          className={`absolute z-20 pointer-events-none px-2 py-1 text-xs rounded shadow ${
            dragRailMeasure.valid === false
              ? 'bg-amber-500/95 text-white'
              : 'bg-gray-900/85 text-white'
          }`}
          style={{
            left: dragRailMeasure.x + 12,
            top: dragRailMeasure.y - 18,
          }}
        >
          {dragRailMeasure.text}
        </div>
      )}

      {Number.isFinite(gridsPerMeter) && gridsPerMeter > 0 && (
        <div className="absolute bottom-4 right-4 z-10 group">
          <button
            type="button"
            aria-label="Unit calibration details"
            title="Unit calibration"
            className="w-7 h-7 rounded-full bg-white/95 dark:bg-gray-900/95 border border-gray-200 dark:border-gray-700 shadow-lg text-gray-700 dark:text-gray-200 text-sm font-semibold"
          >
            i
          </button>

          <div className="absolute bottom-9 right-0 hidden group-hover:block w-[320px] bg-white/95 dark:bg-gray-900/95 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 text-xs text-gray-800 dark:text-gray-100">
            <div className="font-semibold mb-1">Authoritative unit calibration</div>
            <div>1 unit = {pixelsPerUnit.toFixed(2)} px (zoom)</div>
            <div>1 unit = {unitPerWorldLabel}</div>
            <div>
              1 grid = {gridUnits.toFixed(2)} units = {metersPerGrid.toFixed(4)} m ={' '}
              {pixelsPerGrid.toFixed(2)} px
            </div>
            <div className="mt-2 font-semibold">
              1 meter = {unitsPerMeter.toFixed(4)} units = {gridsPerMeter.toFixed(4)} grids ={' '}
              {pixelsPerMeter.toFixed(2)} px
            </div>
            <div>1 px = {metersPerPixel.toFixed(6)} m</div>
            <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 font-semibold">
              Drawing calibration
            </div>
            {fpCalibration ? (
              <>
                <div>
                  Source: {fpCalibration.source}
                  {fpCalibration.resolved
                    ? ` (${Math.round(fpCalibration.confidence * 100)}% confidence)`
                    : ' — units unknown'}
                </div>
                {fpCalibration.resolved && (
                  <>
                    <div>
                      Detected unit: {fpCalibration.unit ?? 'non-standard'} ={' '}
                      {fpCalibration.millimetersPerDrawingUnit?.toFixed(4)} mm/drawing unit
                    </div>
                    {fpMmPerCanvasUnit != null && (
                      <div>1 canvas unit = {fpMmPerCanvasUnit.toFixed(4)} mm</div>
                    )}
                  </>
                )}
              </>
            ) : (
              <div>No imported drawing.</div>
            )}
          </div>
        </div>
      )}



      {pendingCircleJunctionDrop && (
        <div
          className="absolute z-30 w-56 rounded-xl border border-slate-200 bg-white/95 p-3 shadow-xl"
          style={{
            left: Math.min(Math.max(12, pendingCircleJunctionDrop.uiPt.x + 10), stageSize.width - 240),
            top: Math.min(Math.max(12, pendingCircleJunctionDrop.uiPt.y + 10), stageSize.height - 170),
          }}
        >
          <div className="text-xs font-semibold text-slate-800 mb-2">Circle Junction Ports</div>
          <div className="space-y-2">
            <label className="block">
              <span className="text-[11px] text-slate-600">Port Count (1-8)</span>
              <input
                type="number"
                min={1}
                max={8}
                value={pendingCircleJunctionDrop.portCount}
                onChange={(evt) =>
                  setPendingCircleJunctionDrop((prev) =>
                    prev ? { ...prev, portCount: evt.target.value } : prev
                  )
                }
                className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
              />
            </label>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setPendingCircleJunctionDrop(null)}
              className="rounded-md border border-slate-300 px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCircleJunctionPopoverApply}
              className="rounded-md bg-sky-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-sky-500"
            >
              Create
            </button>
          </div>
        </div>
      )}

      {pendingStockerDrop && (
        <div
          className="absolute z-30 w-56 rounded-xl border border-slate-200 bg-white/95 p-3 shadow-xl"
          style={{
            left: Math.min(Math.max(12, pendingStockerDrop.uiPt.x + 10), stageSize.width - 240),
            top: Math.min(Math.max(12, pendingStockerDrop.uiPt.y + 10), stageSize.height - 170),
          }}
        >
          <div className="text-xs font-semibold text-slate-800 mb-2">Stocker Bays</div>
          <div className="space-y-2">
            <label className="block">
              <span className="text-[11px] text-slate-600">Number of Bays</span>
              <select
                value={pendingStockerDrop.bayCount}
                onChange={(evt) =>
                  setPendingStockerDrop((prev) =>
                    prev ? { ...prev, bayCount: evt.target.value } : prev
                  )
                }
                className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
              >
                <option value="4">4 Bays (Default)</option>
                <option value="6">6 Bays</option>
                <option value="8">8 Bays</option>
              </select>
            </label>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setPendingStockerDrop(null)}
              className="rounded-md border border-slate-300 px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleStockerPopoverApply}
              className="rounded-md bg-sky-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-sky-500"
            >
              Create
            </button>
          </div>
        </div>
      )}

      {/* Unmount Konva while the 2D shell is CSS-hidden under 3D — drawing into
          a display:none stage produces 0×0 buffer canvases and InvalidStateError
          on Circle.drawScene when the sim keeps the tree hot. */}
      {!suspendKonvaFor3d && stageSize.width > 0 && stageSize.height > 0 && (
      <Stage
        ref={stageRef}
        width={stageSize.width}
        height={stageSize.height}
        scaleX={viewport.zoom}
        scaleY={viewport.zoom}
        x={viewport.panX}
        y={viewport.panY}
        draggable={effectiveTool === 'pan'}
        onClick={handleStageClick}
        onWheel={handleWheel}
        onMouseDown={handleStageMouseDown}
        onMouseMove={handleStageMouseMove}
        onMouseUp={handleStageMouseUp}
        onDragStart={handleStageDragStart}
        onDragEnd={handleStageDragEnd}
        onContextMenu={handleStageContextMenu}
      >
        {/* Background layer: grid + imported floor plan. listening={false} and
            isolated so element hover/select/drag never re-draws the (potentially
            huge) floor plan geometry — only pan/zoom redraws this layer. */}
        <Layer listening={false}>
          {showGrid && (
            <Grid
              stageSize={stageSize}
              stagePos={{ x: viewport.panX, y: viewport.panY }}
              zoomLevel={viewport.zoom}
            />
          )}
          {simulationMode ? (
            <SimulationFloorPlanImage2D navigationPreview={viewportGestureActive} />
          ) : (
            <>
              <FloorPlan />
              <FloorPlanLabels />
            </>
          )}
          <FacilityZones />
          <MindmapHighlightOverlay />
        </Layer>

        <Layer>
          {lockedLayerWrappers.map((wrapper) => {
            const layerName = layoutLayers[wrapper.layerId]?.name || 'Locked Layer';
            const tagPaddingX = 6 / zoomLevel;
            const tagHeight = 18 / zoomLevel;
            const approxCharWidth = 6.5 / zoomLevel;
            const tagWidth = Math.max(
              90 / zoomLevel,
              Math.min(wrapper.width, layerName.length * approxCharWidth + tagPaddingX * 2)
            );
            const isActiveWrapper = activeTransformWrapper?.id === wrapper.layerId;
            const x = isActiveWrapper ? activeTransformWrapper.startX : wrapper.x;
            const y = isActiveWrapper ? activeTransformWrapper.startY : wrapper.y;
            const width = isActiveWrapper ? activeTransformWrapper.startWidth : wrapper.width;
            const height = isActiveWrapper ? activeTransformWrapper.startHeight : wrapper.height;

            return (
              <Group
                key={`locked-layer-wrap-${wrapper.layerId}`}
                ref={(node) => {
                  if (node) wrapperRefs.current[wrapper.layerId] = node;
                  else delete wrapperRefs.current[wrapper.layerId];
                }}
                x={x + width / 2}
                y={y + height / 2}
                offsetX={width / 2}
                offsetY={height / 2}
                listening
                draggable={effectiveTool === 'select'}
                onClick={(e) => {
                  e.cancelBubble = true;
                  if (effectiveTool === 'select') setSelectedLockedLayerId(wrapper.layerId);
                }}
                onTap={(e) => {
                  e.cancelBubble = true;
                  if (effectiveTool === 'select') setSelectedLockedLayerId(wrapper.layerId);
                }}
                onTransformStart={() => handleLockedLayerTransformStart(wrapper.layerId, x, y, width, height)}
                onTransform={(e) => handleLockedLayerTransform(wrapper.layerId, e.target as Konva.Group, { x: x, y: y })}
                onTransformEnd={(e) => handleLockedLayerTransformEnd(wrapper.layerId, e.target as Konva.Group, { x: x, y: y })}
                onDragStart={() => handleLockedLayerDragStart(wrapper.layerId, x, y, width, height)}
                onDragMove={(e) =>
                  handleLockedLayerDragMove(
                    wrapper.layerId,
                    { x: x, y: y },
                    { x: e.target.x() - width / 2, y: e.target.y() - height / 2 }
                  )
                }
                onDragEnd={(e) =>
                  handleLockedLayerDragEnd(
                    wrapper.layerId,
                    { x: x, y: y },
                    { x: e.target.x() - width / 2, y: e.target.y() - height / 2 }
                  )
                }
              >
                <Rect
                  x={0}
                  y={0}
                  width={width}
                  height={height}
                  fill="rgba(17, 24, 39, 0.08)"
                  stroke="rgba(75, 85, 99, 0.75)"
                  strokeWidth={1.5 / zoomLevel}
                  dash={[8 / zoomLevel, 5 / zoomLevel]}
                />
                <Rect
                  x={0}
                  y={-tagHeight}
                  width={tagWidth}
                  height={tagHeight}
                  fill="rgba(55, 65, 81, 0.92)"
                  stroke="rgba(107, 114, 128, 0.9)"
                  strokeWidth={1 / zoomLevel}
                  cornerRadius={3 / zoomLevel}
                />
                <KonvaText
                  x={tagPaddingX}
                  y={-tagHeight + 3 / zoomLevel}
                  text={`Locked: ${layerName}`}
                  fontSize={11 / zoomLevel}
                  fill="#f9fafb"
                  listening={false}
                />
              </Group>
            );
          })}

          {selectedLockedLayerId && (
            <Transformer
              ref={layerTransformerRef}
              boundBoxFunc={(oldBox, newBox) => {
                const minPx = 10 * Math.max(zoomLevel, 1e-6);
                if (newBox.width < minPx || newBox.height < minPx) return oldBox;
                return newBox;
              }}
              // Transformer ignores the stage scale, so these are screen pixels.
              borderStrokeWidth={2}
              anchorStrokeWidth={2}
              anchorSize={12}
              rotateEnabled={true}
              rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
              rotationSnapTolerance={45}
            />
          )}

          {/* Identified equipment (semantic overlay). Drawn before layout
              elements so rails stay visible and keep winning clicks where an
              equipment footprint overlaps them. */}
          <EquipmentOverlay
            spacebarPan={spacebarPan}
            onContextMenu={handleEquipmentContextMenu}
            onHover={handleEquipmentHover}
            onHoverEnd={handleEquipmentHoverEnd}
          />

          {/* Render non-OHT elements in order; skip hidden elements and hidden layers.
              Visible OHTs are painted on a dedicated layer above this one. */}
          {elementOrder.map((elementId) => {
            const element = elements[elementId];
            if (!element || !element.metadata.visible) return null;
            if (element.type === 'oht-vehicle') return null;
            if (isLayoutElementHiddenByEquipmentOverlay(element, eqInstances, eqVisible)) {
              return null;
            }
            const layer = element.metadata.layerId ? layoutLayers[element.metadata.layerId] : null;
            if (layer && !layer.visible) return null;

            return (
              <CanvasElement
                key={element.id}
                element={element}
                isSelected={selectedIds.includes(element.id)}
                spacebarPan={spacebarPan}
                onContextMenu={handleElementContextMenu}
                zoomLevel={zoomLevel}
              />
            );
          })}

          {/* Live preview of the equipment footprint being drawn */}
          {eqDraw.active && (
            <EquipmentDrawOverlay
              points={eqDraw.points}
              cursor={eqCursor}
              zoom={viewport.zoom}
            />
          )}

          {/* Ghost of the toolbox track being dragged in, drawn with the real
              element renderer at its resolved (snapped) placement. */}
          {dropPreview && (
            <Group listening={false} opacity={0.55}>
              <CanvasElement
                element={dropPreview.element}
                isSelected={false}
                zoomLevel={zoomLevel}
              />
              {/* Junction legs the drop would reveal (e.g. a 45° diagonal port). */}
              {dropPreview.junctionStubs.map((stub, i) => (
                <Line
                  key={`drop-stub-${i}`}
                  points={stub.points}
                  stroke={stub.color}
                  strokeWidth={stub.strokeWidth}
                  lineCap="round"
                  lineJoin="round"
                />
              ))}
              {dropPreview.snapPoint && (
                <Group
                  x={dropPreview.snapPoint.x}
                  y={dropPreview.snapPoint.y}
                  listening={false}
                >
                  <Circle
                    radius={9 / zoomLevel}
                    fill="rgba(16, 185, 129, 0.25)"
                    stroke="#10b981"
                    strokeWidth={2 / zoomLevel}
                  />
                  <Circle radius={3 / zoomLevel} fill="#10b981" />
                </Group>
              )}
            </Group>
          )}

          {/* Simulation track-network nodes (same ids sent to AMHS API) */}
          <SimulationTrackNodeOverlay zoomLevel={zoomLevel} />

          {/* Valid click target while manually adding a track-network node */}
          {trackNodePreview && (
            <Group
              x={trackNodePreview.x}
              y={trackNodePreview.y}
              listening={false}
            >
              <Circle
                radius={8 / zoomLevel}
                fill="rgba(250, 204, 21, 0.25)"
                stroke="#ca8a04"
                strokeWidth={1.5 / zoomLevel}
                dash={[3 / zoomLevel, 3 / zoomLevel]}
              />
              <Circle
                radius={4 / zoomLevel}
                fill="#facc15"
                stroke="#ca8a04"
                strokeWidth={1 / zoomLevel}
              />
            </Group>
          )}

          {/* Route start/end markers — one teal START pin and one rose END pin. */}
          {(() => {
            const byRole: Record<'start' | 'end', { x: number; y: number } | null> = {
              start: null,
              end: null,
            };
            for (const e of Object.values(elements)) {
              const role = e.customProperties?.endpointRole;
              if (role === 'start' || role === 'end') {
                byRole[role] = {
                  x: e.position.x + (e.dimensions.width ?? 0) / 2,
                  y: e.position.y + (e.dimensions.height ?? 0) / 2,
                };
              }
            }
            // While new points are being picked — and until the route built from them loads (that
            // load resets these) — the points just clicked win. Otherwise the previous route's
            // start/end nodes kept showing and the new clicks looked ignored, even though the
            // generation already used them.
            if (routeEndpointPlacement || routeStartPoint || routeEndPoint) {
              byRole.start = routeStartPoint;
              byRole.end = routeEndPoint;
            }
            return (['start', 'end'] as const).map((role) => {
              const m = byRole[role];
              if (!m) return null;
              const Marker = role === 'start' ? StartNode2DShape : EndNode2DShape;
              return (
                <Group key={`route-endpoint-${role}`} x={m.x} y={m.y} listening={false}>
                  <Marker scale={1 / zoomLevel} />
                </Group>
              );
            });
          })()}

          {/* Manually-placed stop markers (persistent, numbered per vehicle) */}
          {placedStopMarkers.map((m) =>
            m ? (
              <Group 
                key={`stop-${m.key}`} 
                x={m.x} 
                y={m.y} 
                listening={true}
                onMouseEnter={() => setHoveredStop(m.key)}
                onMouseLeave={() => setHoveredStop(null)}
              >
                <Circle
                  radius={4 / zoomLevel}
                  fill={m.color}
                  stroke="#ffffff"
                  strokeWidth={1.5 / zoomLevel}
                />
                <KonvaText
                  text={String(m.idx)}
                  fontSize={5 / zoomLevel}
                  fontStyle="bold"
                  fill="#ffffff"
                  width={8 / zoomLevel}
                  height={8 / zoomLevel}
                  offsetX={4 / zoomLevel}
                  offsetY={4 / zoomLevel}
                  align="center"
                  verticalAlign="middle"
                  perfectDrawEnabled={false}
                />
                {hoveredStop === m.key && (
                  <Label y={-10 / zoomLevel} listening={false}>
                    <Tag
                      fill="rgba(17, 24, 39, 0.9)"
                      pointerDirection="down"
                      pointerWidth={8 / zoomLevel}
                      pointerHeight={6 / zoomLevel}
                      cornerRadius={4 / zoomLevel}
                    />
                    <KonvaText
                      text={`${m.ohtName}\n${m.stopName}`}
                      fill="white"
                      fontSize={11 / zoomLevel}
                      padding={6 / zoomLevel}
                      align="center"
                    />
                  </Label>
                )}
              </Group>
            ) : null
          )}

          {/* Live preview while placing a stop */}
          {stopPreview && (
            <Group x={stopPreview.x} y={stopPreview.y} listening={false}>
              <Circle
                radius={7 / zoomLevel}
                stroke="#2563eb"
                strokeWidth={1.5 / zoomLevel}
                dash={[3 / zoomLevel, 3 / zoomLevel]}
                fill="rgba(37, 99, 235, 0.15)"
              />
              <Circle radius={2.5 / zoomLevel} fill="#2563eb" />
            </Group>
          )}

          {/* Topology validation overlays (F2.2) */}
          {Array.from(conflictIds).map((id) => {
            const el = elements[id];
            if (!el) return null;
            const box = getAABB(el);
            return (
              <Rect
                key={`conflict-${id}`}
                x={box.minX - 3}
                y={box.minY - 3}
                width={box.maxX - box.minX + 6}
                height={box.maxY - box.minY + 6}
                stroke="#ef4444"
                strokeWidth={2}
                dash={[8, 4]}
                listening={false}
              />
            );
          })}

          {validation.openEndpoints.map((p, i) => (
            <Circle
              key={`open-${i}`}
              x={p.x}
              y={p.y}
              radius={7}
              stroke="#f59e0b"
              strokeWidth={2}
              fill="rgba(245, 158, 11, 0.25)"
              listening={false}
            />
          ))}

          {/* Rubber-band selection rectangle */}
          {selectionRect && (
            <Rect
              x={selectionRect.x}
              y={selectionRect.y}
              width={selectionRect.width}
              height={selectionRect.height}
              fill="rgba(59, 130, 246, 0.08)"
              stroke="#3b82f6"
              strokeWidth={1 / zoomLevel}
              dash={[6 / zoomLevel, 4 / zoomLevel]}
              listening={false}
            />
          )}

          {/* Highlight the floor-plan polyline currently under the cursor */}
          {hoverData && (
            <Line
              points={hoverData.points}
              stroke="#f59e0b"
              strokeWidth={2.5 / zoomLevel}
              hitStrokeWidth={0}
              listening={false}
              perfectDrawEnabled={false}
            />
          )}

          {/* Selected facility zone outline (persistent) */}
          {selectedRegion && (
            <Rect
              x={selectedRegion.x}
              y={selectedRegion.y}
              width={selectedRegion.width}
              height={selectedRegion.height}
              stroke="#0ea5e9"
              strokeWidth={3 / zoomLevel}
              dash={[10 / zoomLevel, 5 / zoomLevel]}
              listening={false}
              perfectDrawEnabled={false}
            />
          )}

          {/* Hovered facility zone outline */}
          {zoneHoverData && (
            <Rect
              x={zoneHoverData.region.x}
              y={zoneHoverData.region.y}
              width={zoneHoverData.region.width}
              height={zoneHoverData.region.height}
              stroke="#f59e0b"
              strokeWidth={2.5 / zoomLevel}
              listening={false}
              perfectDrawEnabled={false}
            />
          )}
        </Layer>

        {/* Design View: dedicated OHT layer so visible vehicles always sit above
            rails, stockers, equipment, and other design overlays. */}
        {!suspendKonvaFor3d && !simulationRunning && (
          <Layer listening={true}>
            {elementOrder.map((elementId) => {
              const element = elements[elementId];
              if (!element || element.type !== 'oht-vehicle' || !element.metadata.visible) {
                return null;
              }
              const layer = element.metadata.layerId ? layoutLayers[element.metadata.layerId] : null;
              if (layer && !layer.visible) return null;

              return (
                <CanvasElement
                  key={element.id}
                  element={element}
                  isSelected={selectedIds.includes(element.id)}
                  spacebarPan={spacebarPan}
                  onContextMenu={handleElementContextMenu}
                  zoomLevel={zoomLevel}
                />
              );
            })}
          </Layer>
        )}

        {/* Keep dynamic OHT paint isolated: its per-frame batchDraw must not
            repaint segment paths or inventory badges on complex layouts. */}
        {simulationRunning && !suspendKonvaFor3d && (
          <>
          <Layer listening={false}>
            <SimulationSegmentHighlight zoomLevel={zoomLevel} />
          </Layer>
          <Layer listening={true}>
            <SimulationOverlay
              zoomLevel={zoomLevel}
              navigationPreview={viewportGestureActive}
            />
          </Layer>
          <Layer listening={false}>
            <SimulationInventoryOverlay zoomLevel={zoomLevel} />
            <SimulationPickupReadyOverlay zoomLevel={zoomLevel} />
          </Layer>
          </>
        )}

        {/* Animated Point Draw overlay on its own layer, so its per-frame
            animation only redraws these decorative nodes. */}
        <Layer listening={false}>
          {pointDraw.active && (
            <PointDrawOverlay
              zoom={zoomLevel}
              vertices={pointDraw.vertices}
              preview={pointDrawCloseIntent?.active ? null : pointDrawPreview}
              snapPoint={pointDrawCloseIntent?.active ? null : pointDrawSnapPoint}
              ports={portHints.ports}
              activeTip={portHints.activeTip}
              route={portHints.route}
              centerline={pointDrawCloseIntent?.active ? null : pointDrawCenterline}
              closeIntent={pointDrawCloseIntent}
            />
          )}
        </Layer>
      </Stage>
      )}

      {/* Floating inspector for the hovered floor-plan entity */}
      {hoverData && (
        <FloorPlanTooltip
          x={hoverData.sx}
          y={hoverData.sy}
          containerWidth={stageSize.width}
          containerHeight={stageSize.height}
          type={hoverData.type}
          entityColor={hoverData.entityColor}
          layerName={hoverData.layerName}
          layerColor={hoverData.layerColor}
          category={hoverData.category}
          subcategory={hoverData.subcategory}
          layerVisible={hoverData.layerVisible}
          frozen={hoverData.frozen}
          locked={hoverData.locked}
          pointCount={hoverData.pointCount}
          width={hoverData.width}
          height={hoverData.height}
          scaleFactor={fpScaleFactor}
          coordX={hoverData.coordX}
          coordY={hoverData.coordY}
          startX={hoverData.startX}
          startY={hoverData.startY}
          endX={hoverData.endX}
          endY={hoverData.endY}
        />
      )}

      {elementContextMenu && (
        <ElementContextMenu
          menu={elementContextMenu}
          containerWidth={stageSize.width}
          containerHeight={stageSize.height}
          onClose={() => setElementContextMenu(null)}
        />
      )}

      {equipmentContextMenu && (
        <EquipmentContextMenu
          menu={equipmentContextMenu}
          containerWidth={stageSize.width}
          containerHeight={stageSize.height}
          onClose={() => setEquipmentContextMenu(null)}
        />
      )}

      {/* Floating inspector for the hovered facility zone */}
      {zoneHoverData && (
        <FacilityZoneTooltip
          x={zoneHoverData.sx}
          y={zoneHoverData.sy}
          containerWidth={stageSize.width}
          containerHeight={stageSize.height}
          name={zoneHoverData.region.name}
          type={zoneHoverData.region.type}
          confidence={zoneHoverData.region.confidence}
          equipmentCount={zoneHoverData.region.equipmentCount}
          layer={zoneHoverData.region.layer}
          width={zoneHoverData.region.width}
          height={zoneHoverData.region.height}
          scaleFactor={fpScaleFactor}
        />
      )}

      {equipmentHoverData && (
        <EquipmentTooltip
          x={equipmentHoverData.sx}
          y={equipmentHoverData.sy}
          containerWidth={stageSize.width}
          containerHeight={stageSize.height}
          name={equipmentHoverData.name}
          objectType={equipmentHoverData.objectType}
          category={equipmentHoverData.category}
          process={equipmentHoverData.process}
          description={equipmentHoverData.description}
          tags={equipmentHoverData.tags}
          templateName={equipmentHoverData.templateName}
          rotation={equipmentHoverData.rotation}
          portCount={equipmentHoverData.portCount}
          notes={equipmentHoverData.notes}
          color={equipmentHoverData.color}
          coordX={equipmentHoverData.coordX}
          coordY={equipmentHoverData.coordY}
          width={equipmentHoverData.width}
          height={equipmentHoverData.height}
          sourceLabel={equipmentHoverData.sourceLabel}
        />
      )}
    </div>
  );
};

export default Canvas;
